/**
 * Tesla Coil and Meteor.
 * The coil is a thrown canister that sticks and arcs.
 * The meteor is a marked falling rock that craters, bounces, and rolls a trench.
 */
import { bayer, hex } from "../shared/color";
import { CELL, GRAVITY, SOLID } from "../shared/defs";
import type { Letters, Particles } from "./entities";
import type { PageFight } from "./challenge";
import { meteorPit } from "./crater";
import type { PixBuf } from "./rig";
import type { Slabs } from "./slabs";
import type { World } from "./world";

/** Tuning for one canister. Cooldown and throw speed live on the weapon def (1.6s, 620). */
export const TESLA = {
  life: 3,
  gapMin: 0.13,
  gapMax: 0.19,
  reach: 150,
  chainReach: 80,
  /** Main strikes look out to farReach for text when fewer than `starve` glyphs are in reach. */
  starve: 6,
  farReach: 230,
  chip: 11,
  blockDmg: 18,
  minionDmg: 28,
  minionCap: 168,
  popRadius: 9,
  flight: 1.45,
  boltLife: 0.1,
  /** Hard stop on terrain chips so dense pages stay under the paid clear cap. */
  carveBudget: 36,
  /** Chance of a second and third hop off the first strike. */
  hop2: 0.8,
  hop3: 0.4,
  /** Loose letters within `shake` px of a strike, each knocked off with chance `shakeOdds`. */
  shake: 36,
  shakeOdds: 0.6,
  /** Chance a strike goes for page text in reach instead of a block. */
  letterOdds: 0.65,
  /** Distance falloff for picking strikes: weight is 1 / (distance + near). */
  near: 40,
  /** Blocks over BREAK_AREA one coil may finish. */
  breaks: 4,
  /** Landing shock: blocks within `shockR` px of the canister take `shockDmg`, fading to the edge. */
  shockR: 84,
  shockDmg: 75,
  /** Loose letters within this many px of the canister fly off when it lands. */
  shockLetters: 48,
  /** Only blocks within this many px of the canister can be finished by it; farther ones crack. */
  finishR: 50,
  /** Longest throw-moment bolt out of the hand. */
  throwBolt: 96,
  /** Visual bolts in the burst when the canister sticks. */
  plantBolts: 6,
  /** Idle arcs per second while live, and how long each one shows. */
  crawlRate: 120,
  crawlLife: 0.1,
  /** Chance an idle arc that lands on a glyph knocks it loose. */
  crawlKnock: 0.4,
  /** Seconds between refreshes of the idle arcs' targets. */
  sightGap: 0.12,
  /** Strike bolt life: long enough that every frame shows one. */
  strikeLife: 0.24,
  /** Final burst: visual bolts, and real strikes on the nearest things. */
  burstBolts: 11,
  burstHits: 3,
  /** Final pulse reach, and the chance each loose glyph in it is thrown. */
  burstR: 110,
  burstOdds: 0.6,
} as const;

/** Tuning for one meteor. Cooldown lives on the weapon def (1s); casts overlap. */
export const METEOR = {
  /** Ember flight from the staff tip to the aim point. */
  cast: 0.15,
  /** Sigil lights and the rift opens; the rock leaves the rift at cast + summon. */
  summon: 0.3,
  /** Rift to mark. */
  fall: 0.3,
  /** Rock radius in fx pixels (one fx pixel is CELL world px). */
  size: 15,
  gravity: 0.45,
  pit: 78,
  scrape: 20,
  trench: 10,
  trenchMax: 110,
  gougeStep: 9,
  friction: 800,
  drag: 0.95,
  stop: 42,
  knockReach: 360,
  knockPower: 1300,
  knockLift: 0.3,
  minionDmg: 168,
  blockDmg: 140,
  splash: 80,
  fade: 0.42,
  grace: 0.16,
  /** Impact hit-stop. */
  freeze: 0.075,
  /** How long the crater keeps glowing and smoking. */
  smolder: 1.6,
  /** Blocks over BREAK_AREA one meteor may finish. */
  breaks: 3,
  /** The falling rock passes through the page until it is this close above the mark. */
  clear: 14,
} as const;

/**
 * Page-wide bands. One activation (a whole coil or a whole meteor, trench and
 * all) deals a band no more than one rocket hit (WIDE_TOTAL) and may finish
 * at most one band, and only a band it landed on (the meteor's crater, the
 * spot the coil stuck), the way a rocket only finishes a band it hits; the
 * rest only crack. A coil also finishes at most TESLA.breaks blocks over
 * BREAK_AREA, all within TESLA.finishR of where it stuck (about a rocket's
 * splash), since it sits among them for three seconds. A huge band (a hero holding much of the page) is never
 * finished by one activation. Other weapons still finish what these crack.
 * Same areas as the magnet's band rule.
 */
export const WIDE_AREA = 40_000;
export const HUGE_AREA = 120_000;
export const WIDE_TOTAL = 150;

/** Blocks larger than this (px²) count against an activation's `breaks`. Icons and chips do not. */
export const BREAK_AREA = 6_000;

/** Damage ledger for one activation. */
export interface BandLedger {
  dealt: Map<number, number>;
  /** Page-wide bands this activation may still finish. */
  finishes: number;
  /** Bands it actually landed on; only these may be finished, like a rocket's direct hit. */
  home: Set<number>;
  /** Blocks over BREAK_AREA (bands aside) it may still finish. */
  breaks: number;
  /** When set, only blocks within `r` px of (x, y) may be finished, like a rocket's splash. */
  zone?: { x: number; y: number; r: number };
}

const CORE = hex("#fffef6");
const YELL = hex("#ffe56a");
const PALE = hex("#fff3b0");
/** Outer edge of a strike: warm enough to read on a white page, still a glow on a dark one. */
const AMBER = hex("#f2b630");
const SCORCH = hex("#2a120c");
const SCORCH_HOT = hex("#6a3018");
const INK = hex("#141018");
const CHAR = hex("#2a241c");
const CHAR_L = hex("#3c3228");
const UMBER = hex("#6b4a2a");
const UMBER_L = hex("#8a5a32");
const MAGMA = hex("#ff6a1a");
const MAGMA_L = hex("#ffb03a");
const MAGMA_HOT = hex("#fff1c4");
const STEEL_D = hex("#2c3540");
const STEEL = hex("#5c6874");
const STEEL_L = hex("#c5d0da");
const COPPER_D = hex("#7a3e1c");
const COPPER = hex("#c56a32");
const COPPER_L = hex("#f0a060");

export interface Coil {
  x: number;
  y: number;
  vx: number;
  vy: number;
  phase: "fly" | "arc";
  t: number;
  left: number;
  gap: number;
  salt: number;
  hurt: Map<number, number>;
  carved: number;
  bands: BandLedger;
  /** Where the throw was aimed and how long it takes to get there. */
  tx: number;
  ty: number;
  flight: number;
  /** Page points in reach the idle arcs crawl to, refreshed a few times a second. */
  /** Idle-arc targets; a third entry of 1 marks a page glyph. */
  sight: Sight;
  sightT: number;
  /** Recent strike points: arcs keep licking what they just hit. */
  hits: Array<[number, number, number]>;
  /** Idle-arc spawn clock. */
  crawl: number;
}

export interface Bolt {
  pts: Array<[number, number]>;
  life: number;
  max: number;
  salt: number;
  /** Side forks drawn off the main path, 0 to 3. */
  forks?: number;
  /** A damaging strike: wider glow, brighter core. */
  heavy?: boolean;
}

export interface MeteorRock {
  phase: "cast" | "mark" | "fall" | "roll" | "fade" | "gone";
  x: number;
  y: number;
  vx: number;
  vy: number;
  tx: number;
  ty: number;
  /** Staff tip the ember left from. */
  ox: number;
  oy: number;
  /** Rift centre in the sky above the mark. */
  rx: number;
  ry: number;
  fromRight: boolean;
  t: number;
  /** Seconds since the press; never reset. */
  age: number;
  spin: number;
  gouged: number;
  since: number;
  cratered: boolean;
  nudged: boolean;
  grace: number;
  sting: number;
  salt: number;
  bands: BandLedger;
  /** Seconds since impact, or -1. Drives the shockwave and the smoking crater. */
  hit: number;
  hx: number;
  hy: number;
  /** Which rock bitmap this meteor uses. */
  look: number;
}

export interface OrdnanceHost {
  world: World;
  parts: Particles;
  letters: Letters;
  fight: PageFight;
  slabs: Slabs;
  alive: boolean;
  px: number;
  py: number;
  camX: number;
  camY: number;
  viewW: number;
  viewH: number;
  sfx(id: string, pitch?: number): void;
  fling(index: number, vx: number, vy: number, av: number, burn: number): void;
  knock(x: number, y: number, reach: number, power: number, lift: number): void;
  trauma(n: number): void;
  heat(rim: { cells: number[]; ring: number[] }, dur: number, hot: number): void;
  blast(): void;
  flashScreen(amount: number, color: string): void;
  ring(x: number, y: number, r0: number, r1: number, life: number, thick: number, color: number): void;
  flash(x: number, y: number, r: number, life: number): void;
  light(x: number, y: number, r: number, k: number, life: number, color?: number): void;
  spall(x: number, y: number, radius: number, count: number, minR: number, maxR: number): void;
  /** Owner hit-stop. */
  freeze?(seconds: number): void;
  /** The caster's meteor landed: staff gem flash and recover pose. */
  landed?(): void;
  /** Red screen-edge tint, 0..1, for this frame. */
  edge?(k: number): void;
}

interface Strike {
  key: string;
  x: number;
  y: number;
  kind: "block" | "minion" | "letter";
  index: number;
}

/**
 * Throw a canister at (tx, ty). Like the rocket, it flies over the page to the
 * aim point (fight bodies still catch it) and sticks there after `flight` s.
 * The hand crackles with forked bolts the moment it leaves, so the throw reads
 * at once. Push the returned bolts into the bolt list.
 */
export function launchCoil(x: number, y: number, vx: number, vy: number, tx = x, ty = y, flight: number = TESLA.flight): Coil {
  return {
    x, y, vx, vy, phase: "fly", t: 0, left: TESLA.life, gap: 0.05,
    salt: Math.floor(Math.random() * 1e9), hurt: new Map(), carved: 0,
    bands: { dealt: new Map(), finishes: 1, home: new Set(), breaks: TESLA.breaks },
    tx, ty, flight,
    sight: [], sightT: 0, hits: [], crawl: 0,
  };
}

/** Throw-moment flash: forked bolts out of the hand along the throw, sparks, light. */
export function throwCrackle(host: OrdnanceHost, bolts: Bolt[], x: number, y: number, tx: number, ty: number) {
  const ang = Math.atan2(ty - y, tx - x);
  const len = Math.min(Math.hypot(tx - x, ty - y), TESLA.throwBolt);
  for (let n = 0; n < 3; n++) {
    const a = ang + (n - 1) * 0.45 + (Math.random() - 0.5) * 0.3;
    const l = len * (n === 1 ? 1 : 0.55 + Math.random() * 0.25);
    bolts.push({
      pts: [[x, y], [x + Math.cos(a) * l * 0.5 + (Math.random() - 0.5) * 10, y + Math.sin(a) * l * 0.5 + (Math.random() - 0.5) * 10], [x + Math.cos(a) * l, y + Math.sin(a) * l]],
      life: 0.16, max: 0.16, salt: Math.floor(Math.random() * 1e9), forks: n === 1 ? 3 : 2,
    });
  }
  host.light(x, y, 90, 0.85, 0.16, PALE);
  host.trauma(0.12);
  for (let n = 0; n < 12; n++) {
    const a = ang + (Math.random() - 0.5) * 1.8;
    const s = 80 + Math.random() * 180;
    spark(host, x, y, Math.cos(a) * s, Math.sin(a) * s - 30, 0.18 + Math.random() * 0.14);
  }
}

export function markMeteor(ox: number, oy: number, tx: number, ty: number, fromRight: boolean, camY: number, viewW: number): MeteorRock {
  // Rift: high above the mark, in view when possible, with a slight lean so the fall reads as a streak.
  const side = fromRight ? 1 : -1;
  const ry = Math.min(ty - 110, Math.max(camY + 34, ty - 460));
  const rx = tx + side * Math.min(110, viewW * 0.1);
  return {
    phase: "cast", x: ox, y: oy, vx: 0, vy: 0, tx, ty, ox, oy, rx, ry, fromRight,
    t: 0, age: 0, spin: Math.random() * Math.PI * 2, gouged: 0, since: 0,
    cratered: false, nudged: false, grace: 0, sting: 0, salt: Math.floor(Math.random() * 1e9),
    bands: { dealt: new Map(), finishes: 1, home: new Set(), breaks: METEOR.breaks },
    hit: -1, hx: tx, hy: ty, look: (Math.random() * ROCKS.length) | 0,
  };
}

export function stepOrdnance(coils: Coil[], bolts: Bolt[], meteors: MeteorRock[], dt: number, host: OrdnanceHost) {
  for (let i = coils.length - 1; i >= 0; i--) {
    if (!stepCoil(coils[i], dt, host, bolts)) coils.splice(i, 1);
  }
  for (let i = bolts.length - 1; i >= 0; i--) {
    bolts[i].life -= dt;
    if (bolts[i].life <= 0) bolts.splice(i, 1);
  }
  for (let i = meteors.length - 1; i >= 0; i--) {
    if (!stepMeteor(meteors[i], dt, host)) meteors.splice(i, 1);
  }
}

export function drawOrdnance(buf: PixBuf, light: PixBuf, coils: Coil[], bolts: Bolt[], meteors: MeteorRock[], time: number) {
  for (const rock of meteors) {
    drawMeteorFx(buf, light, rock, time);
  }
  for (const coil of coils) drawCanister(buf, light, coil, time);
  for (const bolt of bolts) drawBolt(buf, light, bolt, time);
}

function hash(a: number, b: number, c: number): number {
  let x = Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca6b) ^ Math.imul(c | 0, 0xc2b2ae35);
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

function blocked(world: World, x: number, y: number): boolean {
  return world.solid(Math.floor(x / CELL), Math.floor(y / CELL));
}

/** damageBlock with the page-wide band rule applied for this activation. */
function hurtBlock(world: World, ledger: BandLedger, index: number, amount: number, dirX: number, dirY: number, bx?: number, by?: number, hx?: number, hy?: number) {
  const block = world.blocks[index];
  if (!block?.alive || amount <= 0) return;
  if (!block.canDetach) {
    world.damageBlock(index, amount, dirX, dirY, bx, by, hx, hy);
    return;
  }
  const area = block.w * block.h;
  const wide = area > WIDE_AREA;
  let give = amount;
  const used = ledger.dealt.get(index) ?? 0;
  if (wide) give = Math.min(give, WIDE_TOTAL - used);
  if (give <= 0) return;
  const breaks = block.damage + give >= block.hp || block.lost >= block.cells * 0.38;
  if (breaks) {
    const bandOk = !wide || (ledger.finishes > 0 && area <= HUGE_AREA && ledger.home.has(index));
    const countOk = wide || area <= BREAK_AREA || ledger.breaks > 0;
    const z = ledger.zone;
    const zoneOk = !z || Math.hypot(Math.max(block.x - z.x, 0, z.x - (block.x + block.w)), Math.max(block.y - z.y, 0, z.y - (block.y + block.h))) <= z.r;
    if (bandOk && countOk && zoneOk) {
      if (wide) ledger.finishes--;
      if (!wide && area > BREAK_AREA) ledger.breaks--;
    } else {
      if (block.lost >= block.cells * 0.38) return;
      give = Math.max(0, block.hp - block.damage - 1);
      if (give <= 0) return;
    }
  }
  if (wide) ledger.dealt.set(index, used + give);
  world.damageBlock(index, give, dirX, dirY, bx, by, hx, hy);
}

/** Bands under (x, y): the ones this activation landed on. */
function landOn(world: World, ledger: BandLedger, x: number, y: number, pad: number) {
  world.blocks.forEach((block, index) => {
    if (!block.alive || block.w * block.h <= WIDE_AREA) return;
    if (x >= block.x - pad && x <= block.x + block.w + pad && y >= block.y - pad && y <= block.y + block.h + pad) ledger.home.add(index);
  });
}

function stepCoil(coil: Coil, dt: number, host: OrdnanceHost, bolts: Bolt[]): boolean {
  if (coil.phase === "fly") return flyCoil(coil, dt, host, bolts);
  coil.t += dt;
  coil.left -= dt;
  coil.gap -= dt;
  if (coil.gap <= 0) {
    coil.gap = TESLA.gapMin + Math.random() * (TESLA.gapMax - TESLA.gapMin);
    discharge(coil, host, bolts);
  }
  crawl(coil, dt, host, bolts);
  if (coil.left <= 0) {
    popCoil(coil, host, bolts);
    return false;
  }
  return true;
}

/** Page points within reach for the idle arcs: glyphs, block edges, loose fragments, recent hits. */
type Sight = Array<[number, number, number?]>;

function gatherSight(host: OrdnanceHost, x: number, y: number, reach: number): Sight {
  const out: Sight = [];
  const glyphs: Array<[number, number]> = [];
  host.letters.query(x - reach, y - reach, x + reach, y + reach, (i) => {
    if (glyphs.length >= 48) return;
    const gx = host.letters.x[i] + host.letters.w[i] / 2;
    const gy = host.letters.y[i] + host.letters.h[i] / 2;
    if (Math.hypot(gx - x, gy - y) <= reach) glyphs.push([gx, gy]);
  });
  for (let n = 0; n < 5 && glyphs.length; n++) {
    const g = glyphs[(Math.random() * glyphs.length) | 0];
    out.push([g[0], g[1], 1]);
  }
  const edges: Array<[number, number]> = [];
  for (const block of host.world.blocks) {
    if (!block.alive) continue;
    const cx = Math.max(block.x, Math.min(x, block.x + block.w));
    const cy = Math.max(block.y, Math.min(y, block.y + block.h));
    const d = Math.hypot(cx - x, cy - y);
    if (d > reach || d < 10) continue;
    edges.push([cx, cy]);
  }
  for (let n = 0; n < 4 && edges.length; n++) out.push(edges[(Math.random() * edges.length) | 0]);
  for (const slab of host.slabs.list) {
    if (Math.hypot(slab.x - x, slab.y - y) <= reach) out.push([slab.x, slab.y]);
    if (out.length > 14) break;
  }
  return out;
}

/** Between strikes the canister never goes dark: short arcs crawl to nearby things every frame. */
function crawl(coil: Coil, dt: number, host: OrdnanceHost, bolts: Bolt[]) {
  const [sx, sy] = tipOf(coil);
  coil.sightT -= dt;
  if (coil.sightT <= 0) {
    coil.sightT = TESLA.sightGap;
    coil.sight = gatherSight(host, sx, sy, TESLA.reach);
  }
  for (let i = coil.hits.length - 1; i >= 0; i--) {
    coil.hits[i][2] -= dt;
    if (coil.hits[i][2] <= 0) coil.hits.splice(i, 1);
  }
  coil.crawl += dt * TESLA.crawlRate;
  while (coil.crawl >= 1) {
    coil.crawl -= 1;
    const roll = Math.random();
    let end: [number, number];
    let glyph = false;
    if (coil.hits.length && roll < 0.25) {
      const h = coil.hits[(Math.random() * coil.hits.length) | 0];
      end = [h[0], h[1]];
    } else if (coil.sight.length && roll < 0.65) {
      const s = coil.sight[(Math.random() * coil.sight.length) | 0];
      glyph = s[2] === 1;
      end = glyph ? [s[0], s[1]] : [s[0] + (Math.random() - 0.5) * 8, s[1] + (Math.random() - 0.5) * 8];
    } else {
      // A short tendril off the canister itself.
      const a = Math.random() * Math.PI * 2;
      const l = 18 + Math.random() * 34;
      end = [sx + Math.cos(a) * l, sy + Math.sin(a) * l];
    }
    const mid: [number, number] = [(sx + end[0]) / 2 + (Math.random() - 0.5) * 18, (sy + end[1]) / 2 + (Math.random() - 0.5) * 18];
    bolts.push({ pts: [[sx, sy], mid, end], life: TESLA.crawlLife, max: TESLA.crawlLife, salt: Math.floor(Math.random() * 1e9), forks: 2 });
    if (Math.random() < 0.5) spark(host, end[0], end[1], (Math.random() - 0.5) * 120, -40 - Math.random() * 80, 0.14 + Math.random() * 0.1);
    if (Math.random() < 0.25) host.light(end[0], end[1], 46, 0.55, 0.05, YELL);
    // An arc that lands on text sometimes jolts that glyph loose.
    if (glyph && Math.random() < TESLA.crawlKnock) knockGlyph(host, end[0], end[1], sx, sy);
  }
  host.light(sx, sy, 70, 0.45 + Math.random() * 0.3, 0.03, PALE);
}

function knockGlyph(host: OrdnanceHost, x: number, y: number, fromX: number, fromY: number) {
  let hit = -1;
  host.letters.query(x - 3, y - 3, x + 3, y + 3, (i) => {
    if (hit < 0) hit = i;
  });
  if (hit < 0) return;
  const dx = x - fromX;
  const dy = y - fromY;
  const dist = Math.hypot(dx, dy) || 1;
  const push = 90 + Math.random() * 90;
  host.fling(hit, (dx / dist) * push, (dy / dist) * push - 60, (Math.random() - 0.5) * 16, -1);
}

function flyCoil(coil: Coil, dt: number, host: OrdnanceHost, bolts: Bolt[]): boolean {
  const world = host.world;
  const t0 = coil.t;
  coil.t += dt;
  const steps = Math.max(1, Math.ceil((Math.hypot(coil.vx, coil.vy) * dt) / 5));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    if (t0 + h * (i + 1) >= coil.flight) {
      coil.x = coil.tx;
      coil.y = coil.ty;
      landOn(world, coil.bands, coil.tx, coil.ty, 4);
      const [fx, fy] = freePoint(world, coil.x, coil.y, coil.vx, coil.vy);
      coil.x = fx;
      coil.y = fy;
      plant(coil, host, bolts);
      return true;
    }
    coil.vy += GRAVITY * h;
    const nx = coil.x + coil.vx * h;
    const ny = coil.y + coil.vy * h;
    const hit = host.fight.intercept(coil.x, coil.y, nx, ny);
    if (hit.t >= 0 && hit.index >= 0) {
      coil.x += (nx - coil.x) * hit.t;
      coil.y += (ny - coil.y) * hit.t;
      plant(coil, host, bolts);
      return true;
    }
    coil.x = nx;
    coil.y = ny;
  }
  // Live wire in the air: a short crackle trails the canister every frame.
  const sp = Math.hypot(coil.vx, coil.vy) || 1;
  const back = 18 + Math.random() * 16;
  bolts.push({
    pts: [[coil.x, coil.y], [coil.x - (coil.vx / sp) * back + (Math.random() - 0.5) * 14, coil.y - (coil.vy / sp) * back + (Math.random() - 0.5) * 14]],
    life: 0.06, max: 0.06, salt: Math.floor(Math.random() * 1e9), forks: 1,
  });
  spark(host, coil.x, coil.y, -coil.vx * 0.1 + (Math.random() - 0.5) * 80, -coil.vy * 0.1 + (Math.random() - 0.5) * 80, 0.16);
  host.light(coil.x, coil.y, 40, 0.6, 0.04, PALE);
  if (coil.x < -80 || coil.x > world.W + 80 || coil.y > world.H + 60) return false;
  return true;
}

function freePoint(world: World, x: number, y: number, vx: number, vy: number): [number, number] {
  const len = Math.hypot(vx, vy) || 1;
  let px = x;
  let py = y;
  for (let i = 0; i < 14; i++) {
    if (!blocked(world, px, py)) break;
    px -= (vx / len) * 2;
    py -= (vy / len) * 2;
  }
  const ground = world.findSurface(px - 4, px + 4, py - 8, py + 10);
  if (!Number.isNaN(ground) && ground >= py - 2 && ground <= py + 10) py = ground - 1;
  return [px, py];
}

function plant(coil: Coil, host: OrdnanceHost, bolts: Bolt[]) {
  coil.phase = "arc";
  coil.vx = 0;
  coil.vy = 0;
  coil.t = 0;
  coil.left = TESLA.life;
  coil.gap = TESLA.gapMin;
  landOn(host.world, coil.bands, coil.x, coil.y, 4);
  coil.bands.zone = { x: coil.x, y: coil.y, r: TESLA.finishR };
  host.sfx("teslaPlant", 0.94 + Math.random() * 0.1);
  host.trauma(0.16);
  host.parts.smoke(coil.x, coil.y, (Math.random() - 0.5) * 40, -20, 3, 0.35, "#5a4636");
  // It lands live: a ring of forked bolts plus the first real strike, same frame.
  const [sx, sy] = tipOf(coil);
  coil.sight = gatherSight(host, sx, sy, TESLA.reach);
  coil.sightT = TESLA.sightGap;
  burst(host, bolts, sx, sy, coil.sight, TESLA.plantBolts, 0.18, 70);
  const world = host.world;
  world.blocks.forEach((block, index) => {
    if (!block.alive) return;
    const dx = Math.max(block.x - coil.x, 0, coil.x - (block.x + block.w));
    const dy = Math.max(block.y - coil.y, 0, coil.y - (block.y + block.h));
    const dist = Math.hypot(dx, dy);
    if (dist < TESLA.shockR) hurtBlock(world, coil.bands, index, TESLA.shockDmg * (1 - (0.6 * dist) / TESLA.shockR), 0, -1, coil.x, coil.y);
  });
  const kick = TESLA.shockLetters;
  host.letters.query(coil.x - kick, coil.y - kick, coil.x + kick, coil.y + kick, (i) => {
    const gx = host.letters.x[i] + host.letters.w[i] / 2;
    const gy = host.letters.y[i] + host.letters.h[i] / 2;
    const dist = Math.hypot(gx - coil.x, gy - coil.y) || 1;
    if (dist > kick) return;
    const push = 260 * (1 - dist / kick) + 60;
    host.fling(i, ((gx - coil.x) / dist) * push, ((gy - coil.y) / dist) * push - 60, (Math.random() - 0.5) * 18, -1);
  });
  host.light(sx, sy, 120, 0.95, 0.22, PALE);
  for (let n = 0; n < 10; n++) {
    const a = Math.random() * Math.PI * 2;
    const s = 60 + Math.random() * 160;
    spark(host, sx, sy, Math.cos(a) * s, Math.sin(a) * s - 40, 0.2 + Math.random() * 0.12);
  }
  discharge(coil, host, bolts);
}

function discharge(coil: Coil, host: OrdnanceHost, bolts: Bolt[]) {
  const [sx, sy] = tipOf(coil);
  const used = new Set<string>();
  const hops: Strike[] = [];
  const first = pickTarget(host, sx, sy, TESLA.reach, used);
  if (first) {
    hops.push(first);
    used.add(first.key);
    let cx = first.x;
    let cy = first.y;
    const extra = Math.random() < TESLA.hop2 ? (Math.random() < TESLA.hop3 ? 2 : 1) : 0;
    for (let i = 0; i < extra; i++) {
      const next = pickTarget(host, cx, cy, TESLA.chainReach, used);
      if (!next) break;
      hops.push(next);
      used.add(next.key);
      cx = next.x;
      cy = next.y;
    }
  }
  const pts: Array<[number, number]> = [[sx, sy]];
  if (!hops.length) {
    const ang = Math.random() * Math.PI * 2;
    const len = 36 + Math.random() * 44;
    pts.push([sx + Math.cos(ang) * len, sy + Math.sin(ang) * len]);
  } else {
    for (const hop of hops) {
      pts.push([hop.x, hop.y]);
      chip(coil, hop, host);
    }
  }
  bolts.push({ pts, life: TESLA.strikeLife, max: TESLA.strikeLife, salt: Math.floor(Math.random() * 1e9), forks: 3, heavy: true });
  for (const hop of hops) {
    coil.hits.push([hop.x, hop.y, 0.3]);
    // Sympathetic arcs: the strike jumps on to whatever is next to it.
    for (let n = 0; n < 2 && coil.sight.length; n++) {
      const s = coil.sight[(Math.random() * coil.sight.length) | 0];
      if (Math.hypot(s[0] - hop.x, s[1] - hop.y) > TESLA.chainReach * 1.2) continue;
      bolts.push({ pts: [[hop.x, hop.y], [s[0], s[1]]], life: TESLA.strikeLife * 0.7, max: TESLA.strikeLife * 0.7, salt: Math.floor(Math.random() * 1e9), forks: 2 });
    }
  }
  if (coil.hits.length > 6) coil.hits.splice(0, coil.hits.length - 6);
  host.sfx("teslaCrackle", 0.9 + Math.random() * 0.22);
}

function pickTarget(host: OrdnanceHost, x: number, y: number, reach: number, used: Set<string>): Strike | null {
  const blocks: Strike[] = [];
  const world = host.world;
  for (let i = 0; i < world.blocks.length; i++) {
    const block = world.blocks[i];
    if (!block.alive) continue;
    const key = `b${i}`;
    if (used.has(key)) continue;
    const cx = Math.max(block.x, Math.min(x, block.x + block.w));
    const cy = Math.max(block.y, Math.min(y, block.y + block.h));
    const dist = Math.hypot(cx - x, cy - y);
    if (dist > reach) continue;
    let hx = cx;
    let hy = cy;
    if (dist < 6) {
      const ang = Math.random() * Math.PI * 2;
      hx = x + Math.cos(ang) * 14;
      hy = y + Math.sin(ang) * 14;
    }
    blocks.push({ key, x: hx, y: hy, kind: "block", index: i });
  }
  const minions: Strike[] = [];
  const seen = new Set<number>();
  for (let n = 0; n < 10; n++) {
    const ang = Math.random() * Math.PI * 2;
    const aim = host.fight.aimPoint(x, y, ang, 0.85, reach);
    if (!aim || aim.dist > reach) continue;
    const index = host.fight.nearestBody(aim.x, aim.y, 28);
    if (index < 0 || seen.has(index)) continue;
    const key = `m${index}`;
    if (used.has(key)) continue;
    seen.add(index);
    minions.push({ key, x: aim.x, y: aim.y, kind: "minion", index });
  }
  const glyphs: Strike[] = [];
  const findGlyphs = (r: number) => host.letters.query(x - r, y - r, x + r, y + r, (i) => {
    const gx = host.letters.x[i] + host.letters.w[i] / 2;
    const gy = host.letters.y[i] + host.letters.h[i] / 2;
    const key = `l${i}`;
    if (used.has(key) || Math.hypot(gx - x, gy - y) > r) return;
    glyphs.push({ key, x: gx, y: gy, kind: "letter", index: i });
  });
  findGlyphs(reach);
  // Once the text round the canister is gone, main strikes reach farther for more.
  if (glyphs.length < TESLA.starve && reach >= TESLA.reach) {
    glyphs.length = 0;
    findGlyphs(TESLA.farReach);
  }
  let pool = blocks;
  if (minions.length && Math.random() < 0.42) pool = minions;
  else if (glyphs.length && (!blocks.length || Math.random() < TESLA.letterOdds)) pool = glyphs;
  else if (!blocks.length) pool = minions;
  if (!pool.length) return null;
  if (pool === minions) return pool[(Math.random() * pool.length) | 0];
  // Lightning takes the short way: nearer targets are likelier, so strikes gather round the canister.
  let sum = 0;
  const weight = pool.map((s) => (sum += 1 / (Math.hypot(s.x - x, s.y - y) + TESLA.near)));
  const roll = Math.random() * sum;
  return pool[Math.max(0, weight.findIndex((w) => w >= roll))];
}

function chip(coil: Coil, at: Strike, host: OrdnanceHost) {
  const { x, y } = at;
  const world = host.world;
  const owners = new Set<number>();
  const canCarve = coil.carved < TESLA.carveBudget;
  if (canCarve) {
    coil.carved++;
    world.beginCarve();
    world.log?.ctx(1, x, y, TESLA.chip, 0.35);
    world.carve(x, y, TESLA.chip, 0.42, (_cx, _cy, _color, _mat, owner) => {
      if (owner >= 0) owners.add(owner);
    });
    world.scorch(x, y, TESLA.chip * 0.5, TESLA.chip * 1.35, 0.9);
    const edge = world.rim(2);
    world.char(edge.cells, edge.ring);
    host.heat(edge, 0.28, 0.35);
    world.log?.heat(2, 0.28, 0.35, true);
  }
  if (at.kind === "block") owners.add(at.index);
  const dx = x - coil.x;
  const dy = y - coil.y;
  const len = Math.hypot(dx, dy) || 1;
  for (const owner of owners) hurtBlock(world, coil.bands, owner, canCarve ? TESLA.blockDmg : TESLA.blockDmg * 0.55, dx / len, dy / len, x, y, x, y);
  if (at.kind === "minion") {
    const dealt = coil.hurt.get(at.index) ?? 0;
    const dmg = Math.min(TESLA.minionDmg, Math.max(0, TESLA.minionCap - dealt));
    if (dmg > 0) {
      coil.hurt.set(at.index, dealt + dmg);
      host.fight.hurt(x, y, 10, dmg, at.index);
    }
  }
  const shake = TESLA.shake;
  host.letters.query(x - shake, y - shake, x + shake, y + shake, (i) => {
    if (Math.random() > TESLA.shakeOdds) return;
    const gx = host.letters.x[i] + host.letters.w[i] / 2;
    const gy = host.letters.y[i] + host.letters.h[i] / 2;
    const lx = gx - x;
    const ly = gy - y;
    const dist = Math.hypot(lx, ly) || 1;
    if (dist > shake) return;
    host.fling(i, (lx / dist) * 130, (ly / dist) * 110 - 50, (Math.random() - 0.5) * 14, -1);
  });
  host.light(x, y, 110, 0.9, 0.14, YELL);
  for (let n = 0; n < 7; n++) {
    const a = Math.random() * Math.PI * 2;
    const s = 50 + Math.random() * 130;
    spark(host, x, y, Math.cos(a) * s, Math.sin(a) * s - 30, 0.2 + Math.random() * 0.14);
  }
  for (let n = 0; n < 3; n++) {
    const life = 0.35 + Math.random() * 0.3;
    host.parts.spawn({
      x, y, vx: (Math.random() - 0.5) * 70, vy: -30 - Math.random() * 60,
      life, max: life, size: 1.5, color: Math.random() < 0.5 ? "#ffd27a" : "#ff9a3a",
      grav: -30, rubble: false, glow: true, kind: "ember",
    });
  }
}

/** Forked bolts from (x, y) out to page points (or open air), all at once. */
function burst(host: OrdnanceHost, bolts: Bolt[], x: number, y: number, sight: Sight, n: number, life: number, reach: number) {
  for (let i = 0; i < n; i++) {
    let end: [number, number];
    if (sight.length && i % 3 !== 2) {
      const s = sight[(i * 7 + ((Math.random() * sight.length) | 0)) % sight.length];
      end = [s[0], s[1]];
    } else {
      const a = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const l = reach * (0.55 + Math.random() * 0.45);
      end = [x + Math.cos(a) * l, y + Math.sin(a) * l];
    }
    const mid: [number, number] = [(x + end[0]) / 2 + (Math.random() - 0.5) * 22, (y + end[1]) / 2 + (Math.random() - 0.5) * 22];
    bolts.push({ pts: [[x, y], mid, end], life, max: life, salt: Math.floor(Math.random() * 1e9), forks: 3, heavy: i % 2 === 0 });
    spark(host, end[0], end[1], (Math.random() - 0.5) * 160, -60 - Math.random() * 90, 0.2 + Math.random() * 0.12);
  }
}

function popCoil(coil: Coil, host: OrdnanceHost, bolts: Bolt[]) {
  const x = coil.x;
  const y = coil.y;
  host.sfx("teslaPop");
  // Last gasp: every arc at once, and a few real strikes on the nearest things.
  const [sx, sy] = tipOf(coil);
  const sight = gatherSight(host, sx, sy, TESLA.reach);
  burst(host, bolts, sx, sy, sight, TESLA.burstBolts, 0.3, 110);
  const used = new Set<string>();
  for (let n = 0; n < TESLA.burstHits; n++) {
    const hit = pickTarget(host, sx, sy, TESLA.reach, used);
    if (!hit) break;
    used.add(hit.key);
    bolts.push({ pts: [[sx, sy], [hit.x, hit.y]], life: 0.3, max: 0.3, salt: Math.floor(Math.random() * 1e9), forks: 3, heavy: true });
    chip(coil, hit, host);
  }
  // The pulse: loose text near the canister is thrown clear, and nearby blocks take one more jolt.
  const pulse = TESLA.burstR;
  host.letters.query(x - pulse, y - pulse, x + pulse, y + pulse, (i) => {
    const gx = host.letters.x[i] + host.letters.w[i] / 2;
    const gy = host.letters.y[i] + host.letters.h[i] / 2;
    const dist = Math.hypot(gx - x, gy - y) || 1;
    if (dist > pulse || Math.random() > TESLA.burstOdds) return;
    const push = 220 * (1 - dist / pulse) + 80;
    host.fling(i, ((gx - x) / dist) * push, ((gy - y) / dist) * push - 70, (Math.random() - 0.5) * 18, -1);
  });
  host.world.blocks.forEach((block, index) => {
    if (!block.alive) return;
    const d = Math.hypot(Math.max(block.x - x, 0, x - (block.x + block.w)), Math.max(block.y - y, 0, y - (block.y + block.h)));
    if (d < pulse) hurtBlock(host.world, coil.bands, index, TESLA.shockDmg * 0.6 * (1 - d / pulse), 0, -1, x, y);
  });
  host.light(sx, sy, 170, 1, 0.3, PALE);
  host.trauma(0.28);
  const world = host.world;
  const owners = new Set<number>();
  world.beginCarve();
  world.log?.ctx(1, x, y, TESLA.popRadius, 0.25);
  world.carve(x, y, TESLA.popRadius, 0.3, (_cx, _cy, _color, _mat, owner) => {
    if (owner >= 0) owners.add(owner);
  });
  world.scorch(x, y, TESLA.popRadius * 0.4, TESLA.popRadius * 1.4, 0.7);
  const edge = world.rim(2);
  world.char(edge.cells, edge.ring);
  host.heat(edge, 0.2, 0.25);
  world.log?.heat(2, 0.2, 0.25, true);
  for (const owner of owners) hurtBlock(world, coil.bands, owner, 16, 0, -1, x, y);
  for (let n = 0; n < 8; n++) {
    const a = Math.random() * Math.PI * 2;
    const s = 60 + Math.random() * 140;
    spark(host, x, y, Math.cos(a) * s, Math.sin(a) * s - 30, 0.22);
  }
}

function spark(host: OrdnanceHost, x: number, y: number, vx: number, vy: number, life: number) {
  host.parts.spawn({
    x, y, vx, vy, life, max: life, size: 1.3,
    color: Math.random() < 0.5 ? "#fffef6" : "#ffe56a",
    grav: 180, rubble: false, glow: true, kind: "spark",
  });
}

function tipOf(coil: Coil): [number, number] {
  const ang = coil.phase === "fly" ? Math.atan2(coil.vy, coil.vx) : -Math.PI / 2;
  const reach = 13 * CELL;
  return [coil.x + Math.cos(ang) * reach, coil.y + Math.sin(ang) * reach];
}

function stepMeteor(rock: MeteorRock, dt: number, host: OrdnanceHost): boolean {
  rock.t += dt;
  rock.age += dt;
  if (rock.hit >= 0) {
    rock.hit += dt;
    smolder(rock, dt, host);
    if (rock.hit < 0.32) host.edge?.(0.9 * (1 - rock.hit / 0.32));
  } else if (rock.age > METEOR.cast && rock.phase !== "fade" && rock.phase !== "gone") {
    host.edge?.(Math.min(1, (rock.age - METEOR.cast) / 0.18) * (rock.phase === "fall" ? 0.85 : 0.6));
  }
  if (rock.phase === "cast") {
    const [x, y] = emberAt(rock, rock.age / METEOR.cast);
    const [px, py] = emberAt(rock, Math.max(0, rock.age - dt) / METEOR.cast);
    rock.x = x;
    rock.y = y;
    for (let n = 0; n < 2; n++) {
      const a = Math.atan2(py - y, px - x) + (Math.random() - 0.5) * 1.2;
      const s = 40 + Math.random() * 120;
      spark(host, x, y, Math.cos(a) * s, Math.sin(a) * s - 20, 0.16 + Math.random() * 0.14);
    }
    if (Math.random() < 0.5) host.parts.smoke(x, y, (Math.random() - 0.5) * 20, -14, 2.5 + Math.random() * 2, 0.35, "#4a3a34");
    if (rock.age >= METEOR.cast) emberLand(rock, host);
    return true;
  }
  if (rock.phase === "mark") {
    riftDrip(rock, host, dt);
    if (rock.age >= METEOR.cast + METEOR.summon) beginFall(rock, host);
    return true;
  }
  if (rock.phase === "gone") return rock.hit >= 0 && rock.hit < METEOR.smolder;
  if (rock.phase === "fade") {
    if (rock.t < METEOR.fade) return true;
    rock.phase = "gone";
    return rock.hit >= 0 && rock.hit < METEOR.smolder;
  }
  rock.spin += dt * (1.4 + Math.hypot(rock.vx, rock.vy) * 0.003);
  if (rock.phase === "roll") return rollMeteor(rock, dt, host);
  if (!rock.cratered) riftDrip(rock, host, dt);
  return fallMeteor(rock, dt, host);
}

/** Ember arc from the staff tip to the mark; u in 0..1. */
function emberAt(rock: MeteorRock, u: number): [number, number] {
  u = Math.max(0, Math.min(1, u));
  const e = 1 - (1 - u) * (1 - u) * 0.35 - (1 - u) * 0.65;
  const k = Math.max(0, Math.min(1, e));
  const dist = Math.hypot(rock.tx - rock.ox, rock.ty - rock.oy);
  const lift = Math.min(110, Math.max(26, dist * 0.32));
  return [rock.ox + (rock.tx - rock.ox) * k, rock.oy + (rock.ty - rock.oy) * k - lift * 4 * k * (1 - k)];
}

function emberLand(rock: MeteorRock, host: OrdnanceHost) {
  rock.phase = "mark";
  rock.t = 0;
  rock.x = rock.tx;
  rock.y = rock.ty;
  host.sfx("meteorRift");
  host.trauma(0.1);
  host.light(rock.tx, rock.ty, 70, 0.8, 0.2, MAGMA_L);
  host.light(rock.rx, rock.ry, 120, 0.5, 0.45, MAGMA);
  for (let n = 0; n < 12; n++) {
    const a = (n / 12) * Math.PI * 2 + Math.random() * 0.4;
    const s = 90 + Math.random() * 160;
    spark(host, rock.tx, rock.ty, Math.cos(a) * s, Math.sin(a) * s - 40, 0.2 + Math.random() * 0.2);
  }
  for (let n = 0; n < 4; n++) host.parts.smoke(rock.tx, rock.ty, (Math.random() - 0.5) * 60, -20 - Math.random() * 30, 3 + Math.random() * 3, 0.5, "#4a3a34");
}

/** Embers and a little smoke leaking out of the open rift. */
function riftDrip(rock: MeteorRock, host: OrdnanceHost, dt: number) {
  const open = riftOpen(rock);
  if (open <= 0.05) return;
  if (Math.random() < dt * 34 * open) {
    const life = 0.35 + Math.random() * 0.3;
    host.parts.spawn({
      x: rock.rx + (Math.random() - 0.5) * 60 * open, y: rock.ry + 4,
      vx: (Math.random() - 0.5) * 40, vy: 40 + Math.random() * 90,
      life, max: life, size: 1.4, color: Math.random() < 0.5 ? "#ff6a1a" : "#ffb03a",
      grav: 120, rubble: false, glow: true, kind: "ember",
    });
  }
}

function riftOpen(rock: MeteorRock): number {
  const a = rock.age - METEOR.cast;
  if (a <= 0) return 0;
  const grow = Math.min(1, a / 0.16);
  const opened = 1 - (1 - grow) * (1 - grow);
  const shut = rock.age - (METEOR.cast + METEOR.summon + 0.12);
  return shut > 0 ? Math.max(0, opened * (1 - shut / 0.24)) : opened;
}

function beginFall(rock: MeteorRock, host: OrdnanceHost) {
  let dx = rock.tx - rock.rx;
  let dy = rock.ty - rock.ry;
  const dist = Math.hypot(dx, dy) || 1;
  const speed = Math.max(900, dist / METEOR.fall);
  rock.x = rock.rx;
  rock.y = rock.ry;
  rock.vx = (dx / dist) * speed;
  rock.vy = (dy / dist) * speed;
  rock.phase = "fall";
  rock.t = 0;
  host.sfx("meteorRush");
  host.trauma(0.12);
  host.light(rock.rx, rock.ry, 140, 0.9, 0.18, PALE);
}

function fallMeteor(rock: MeteorRock, dt: number, host: OrdnanceHost): boolean {
  if (rock.grace > 0) {
    rock.grace -= dt;
    rock.vy += GRAVITY * METEOR.gravity * dt;
    rock.x += rock.vx * dt;
    rock.y += rock.vy * dt;
    trail(host, rock, 2);
    if (rock.y > host.world.H + 90 || rock.x < -240 || rock.x > host.world.W + 240) return fadeMeteor(rock, host, false);
    return true;
  }
  const steps = Math.max(1, Math.ceil((Math.hypot(rock.vx, rock.vy) * dt) / 6));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    if (rock.cratered) rock.vy += GRAVITY * METEOR.gravity * h;
    const nx = rock.x + rock.vx * h;
    const ny = rock.y + rock.vy * h;
    const hit = host.fight.intercept(rock.x, rock.y, nx, ny);
    if (hit.t >= 0 && hit.index >= 0) {
      rock.x += (nx - rock.x) * hit.t;
      rock.y += (ny - rock.y) * hit.t;
      return strikeMeteor(rock, host, "floor", hit.index);
    }
    // The rock drops in from the sky: page above the mark does not stop it, so it lands where it was called.
    const high = !rock.cratered && ny < rock.ty - METEOR.clear;
    const ground = !high && rock.vy > 0 ? host.world.findSurface(nx - 6, nx + 6, Math.min(rock.y, ny) - 2, ny + 4) : NaN;
    if ((!high && blocked(host.world, nx, ny)) || !Number.isNaN(ground)) {
      const kind = strikeKind(host.world, rock, nx, ny);
      rock.x = nx;
      if (!Number.isNaN(ground) && kind === "floor") rock.y = ground - 2;
      else rock.y = ny;
      return strikeMeteor(rock, host, kind, -1);
    }
    rock.x = nx;
    rock.y = ny;
  }
  trail(host, rock, 2);
  if (rock.y > host.world.H + 90 || rock.x < -240 || rock.x > host.world.W + 240) return fadeMeteor(rock, host, false);
  return true;
}

function strikeKind(world: World, rock: MeteorRock, nx: number, ny: number): "floor" | "wall" | "ceil" {
  const below = world.matAt(nx, ny + 6) >= SOLID || !Number.isNaN(world.findSurface(nx - 4, nx + 4, ny, ny + 8));
  const above = world.matAt(nx, ny - 6) >= SOLID;
  if (rock.vy > 0 && below) return "floor";
  if (rock.vy < 0 && above) return "ceil";
  return "wall";
}

function strikeMeteor(rock: MeteorRock, host: OrdnanceHost, kind: "floor" | "wall" | "ceil", direct: number): boolean {
  const heading = Math.atan2(rock.vy, rock.vx);
  if (!rock.cratered) {
    landOn(host.world, rock.bands, rock.x, rock.y, METEOR.pit * 0.35);
    rock.bands.zone = { x: rock.x, y: rock.y, r: METEOR.pit * 0.9 };
    dig(host, rock.x, rock.y, METEOR.pit, heading, {
      bands: rock.bands,
      dmg: METEOR.blockDmg,
      splash: METEOR.splash,
      knock: true,
      direct,
      boom: true,
      spall: 8,
    });
    rock.cratered = true;
    rock.grace = METEOR.grace;
    impactFx(rock, host, heading);
    // Keep the bounce near what the old, slower rock had: the rock now arrives faster.
    const cap = 1300 / Math.max(1300, Math.hypot(rock.vx, rock.vy));
    rock.vx *= cap;
    rock.vy *= cap;
    if (kind === "floor") {
      rock.vy = -Math.abs(rock.vy) * 0.42;
      rock.vx *= 0.8;
      rock.y -= 4;
    } else if (kind === "ceil") {
      rock.vy = Math.abs(rock.vy) * 0.35;
      rock.vx *= 0.7;
      rock.y += 4;
    } else {
      const sign = Math.sign(rock.vx) || 1;
      rock.vx = -sign * Math.abs(rock.vx) * 0.45;
      rock.x -= sign * 4;
    }
    if (Math.hypot(rock.vx, rock.vy) < 160) {
      rock.grace = 0;
      rock.phase = "roll";
      rock.vy = 0;
    }
    dust(host, rock.x, rock.y, 10, 8);
    return true;
  }
  dig(host, rock.x, rock.y, METEOR.scrape, heading, {
    bands: rock.bands,
    dmg: 48,
    splash: 0,
    knock: false,
    direct,
    boom: false,
    spall: 3,
  });
  host.sfx("boomSmall");
  rock.phase = "roll";
  rock.vy = 0;
  rock.t = 0;
  const ground = host.world.findSurface(rock.x - 8, rock.x + 8, rock.y - 16, rock.y + 20);
  if (!Number.isNaN(ground)) rock.y = ground - 2;
  dust(host, rock.x, rock.y, 4, 3);
  return true;
}

function rollMeteor(rock: MeteorRock, dt: number, host: OrdnanceHost): boolean {
  const world = host.world;
  const sign = Math.sign(rock.vx) || 0;
  rock.vx -= sign * METEOR.friction * dt;
  rock.vx *= Math.exp(-METEOR.drag * dt);
  const step = rock.vx * dt;
  const nx = rock.x + step;
  if (blocked(world, nx, rock.y - 2)) {
    rock.vx *= -0.35;
  } else {
    rock.x = nx;
  }
  const ground = world.findSurface(rock.x - 6, rock.x + 6, rock.y - 10, rock.y + 18);
  if (Number.isNaN(ground)) {
    rock.phase = "fall";
    rock.grace = 0;
    rock.vy = 40;
    return true;
  }
  rock.y = ground - 2;
  rock.since += Math.abs(step);
  if (rock.since >= METEOR.gougeStep && rock.gouged < METEOR.trenchMax) {
    rock.since = 0;
    rock.gouged += METEOR.gougeStep;
    const heading = rock.vx >= 0 ? 0 : Math.PI;
    dig(host, rock.x, rock.y, METEOR.trench, heading, {
      bands: rock.bands,
      dmg: 22,
      splash: 0,
      knock: false,
      direct: -1,
      boom: false,
      spall: 0,
    });
    if (Math.random() < 0.55) host.parts.smoke(rock.x, rock.y, -rock.vx * 0.05, -16, 4, 0.45, "#5a4636");
  }
  if (!rock.nudged && host.alive) {
    const dx = host.px - rock.x;
    const dy = host.py - 28 - rock.y;
    if (Math.hypot(dx, dy) < 32) {
      rock.nudged = true;
      host.knock(rock.x, rock.y, 90, 420, 0.15);
    }
  }
  rock.sting -= dt;
  if (rock.sting <= 0) {
    const index = host.fight.nearestBody(rock.x, rock.y, 22);
    if (index >= 0) {
      host.fight.hurt(rock.x, rock.y, 16, 18, index);
      rock.sting = 0.22;
    }
  }
  trail(host, rock, 1);
  if (Math.abs(rock.vx) < METEOR.stop || rock.gouged >= METEOR.trenchMax) return fadeMeteor(rock, host, true);
  if (rock.x < -40 || rock.x > world.W + 40) return fadeMeteor(rock, host, false);
  return true;
}

function fadeMeteor(rock: MeteorRock, host: OrdnanceHost, fizz: boolean): boolean {
  if (fizz) {
    host.sfx("meteorFizz");
    for (let n = 0; n < 6; n++) {
      const a = Math.random() * Math.PI * 2;
      const s = 20 + Math.random() * 70;
      host.parts.spawn({
        x: rock.x, y: rock.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 30,
        life: 0.4, max: 0.4, size: 2, color: n % 2 ? "#3c3228" : "#6b4a2a",
        grav: GRAVITY * 0.45, rubble: true, glow: false, kind: "crumb",
      });
    }
  }
  rock.phase = "fade";
  rock.t = 0;
  rock.vx *= 0.2;
  rock.vy = 0;
  return true;
}

interface DigOpts {
  bands: BandLedger;
  dmg: number;
  splash: number;
  knock: boolean;
  direct: number;
  boom: boolean;
  spall: number;
}

function dig(host: OrdnanceHost, x: number, y: number, radius: number, heading: number, opts: DigOpts) {
  const world = host.world;
  const owners = new Set<number>();
  const note = (_cx: number, _cy: number, _color: number, _mat: number, owner: number) => {
    if (owner >= 0) owners.add(owner);
  };
  world.beginCarve();
  world.log?.ctx(1, x, y, radius, opts.dmg / 150);
  if (opts.spall) host.spall(x, y, radius, opts.spall, 4, radius > 40 ? 11 : 7);
  if (opts.boom) meteorPit(world, x, y, radius, heading, note);
  else world.carve(x, y, radius, 0.38, note);
  world.scorch(x, y, radius * 0.55, radius * 1.4, radius > 40 ? 1 : 0.75);
  const edge = world.rim(radius > 40 ? 3 : 2);
  world.char(edge.cells, edge.ring);
  const hot = radius > 40 ? 0.85 : 0.4;
  const dur = radius > 40 ? 2.2 : 0.45;
  host.heat(edge, dur, hot);
  world.log?.heat(radius > 40 ? 3 : 2, dur, hot, true);
  // Nearest owner first, so the band under the impact is the one a meteor may finish.
  const near = (i: number) => {
    const b = world.blocks[i];
    return Math.hypot(Math.max(b.x - x, 0, x - (b.x + b.w)), Math.max(b.y - y, 0, y - (b.y + b.h)));
  };
  for (const owner of [...owners].sort((a, b) => near(a) - near(b))) hurtBlock(world, opts.bands, owner, opts.dmg, 0, -1, x, y);
  if (opts.splash > 0) {
    const reach = radius * 1.55;
    world.blocks.forEach((block, index) => {
      if (!block.alive || owners.has(index)) return;
      const dx = Math.max(block.x - x, 0, x - (block.x + block.w));
      const dy = Math.max(block.y - y, 0, y - (block.y + block.h));
      const dist = Math.hypot(dx, dy);
      if (dist < reach) hurtBlock(world, opts.bands, index, opts.splash * (1 - dist / reach), 0, -1, x, y);
    });
  }
  const letterReach = radius * (radius > 40 ? 1.3 : 1.05);
  host.letters.query(x - letterReach, y - letterReach, x + letterReach, y + letterReach, (i) => {
    const gx = host.letters.x[i] + host.letters.w[i] / 2;
    const gy = host.letters.y[i] + host.letters.h[i] / 2;
    const dx = gx - x;
    const dy = gy - y;
    const dist = Math.hypot(dx, dy) || 1;
    if (dist > letterReach) return;
    if (radius > 40 && dist < radius * 0.42) {
      host.letters.remove(i);
      return;
    }
    const impulse = (radius > 40 ? 640 : 220) * Math.pow(1 - dist / letterReach, 0.7);
    host.fling(i, (dx / dist) * impulse, (dy / dist) * impulse - 70, (Math.random() - 0.5) * 24, -1);
  });
  if (radius > 40) {
    for (const slab of host.slabs.list.slice()) {
      const dx = slab.x - x;
      const dy = slab.y - y;
      const dist = Math.hypot(dx, dy) || 1;
      const limit = radius * 1.15 + Math.max(slab.bw, slab.bh) * 0.5;
      if (dist > limit) continue;
      if (!slab.fragment) host.slabs.shatter(slab, x, y, 380);
      else {
        slab.vx += (dx / dist) * 420;
        slab.vy += (dy / dist) * 420 - 140;
        slab.av += (Math.random() - 0.5) * 12;
      }
    }
  }
  if (opts.knock) {
    host.knock(x, y, METEOR.knockReach, METEOR.knockPower, METEOR.knockLift);
    host.trauma(1);
    host.fight.hurt(x, y, radius * 1.15, METEOR.minionDmg, opts.direct);
    host.blast();
    host.sfx("boom");
    host.flashScreen(0.72, "255,236,210");
    host.light(x, y, radius * 2.6, 0.8, 0.6, MAGMA);
    host.light(x, y, radius * 1.2, 0.9, 0.1, PALE);
  } else if (opts.direct >= 0) {
    host.fight.hurt(x, y, radius, 36, opts.direct);
  }
}

function trail(host: OrdnanceHost, rock: MeteorRock, smokes: number) {
  const sp = Math.hypot(rock.vx, rock.vy) || 1;
  for (let n = 0; n < smokes; n++) {
    const back = 20 + Math.random() * 60;
    const side = (Math.random() - 0.5) * 22;
    host.parts.smoke(
      rock.x - (rock.vx / sp) * back + (rock.vy / sp) * side,
      rock.y - (rock.vy / sp) * back - (rock.vx / sp) * side,
      -rock.vx * 0.03 + (Math.random() - 0.5) * 50,
      -rock.vy * 0.03 - 10,
      5 + Math.random() * 5,
      0.5 + Math.random() * 0.4,
      Math.random() < 0.5 ? "#5a4636" : "#3c3228",
    );
  }
  if (Math.random() < 0.8) {
    const life = 0.25 + Math.random() * 0.25;
    host.parts.spawn({
      x: rock.x, y: rock.y,
      vx: -rock.vx * 0.08 + (Math.random() - 0.5) * 40,
      vy: -rock.vy * 0.08 - 20,
      life, max: life, size: 1.4,
      color: "#ff6a1a", grav: -40, rubble: false, glow: true, kind: "ember",
    });
  }
}

function dust(host: OrdnanceHost, x: number, y: number, smokes: number, embers: number) {
  for (let n = 0; n < smokes; n++) {
    const a = Math.random() * Math.PI * 2;
    const s = 30 + Math.random() * 110;
    host.parts.smoke(x, y, Math.cos(a) * s, Math.sin(a) * s - 20, 4 + Math.random() * 4, 0.6 + Math.random() * 0.4, "#5a4636");
  }
  for (let n = 0; n < embers; n++) {
    const a = Math.random() * Math.PI * 2;
    const s = 50 + Math.random() * 160;
    const life = 0.3 + Math.random() * 0.35;
    host.parts.spawn({
      x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 40,
      life, max: life, size: 1.6,
      color: n % 2 ? "#ffb03a" : "#ff6a1a", grav: 80, rubble: false, glow: true, kind: "chunk",
    });
  }
}

function buildCoil(): Array<[number, number, number]> {
  const px: Array<[number, number, number]> = [];
  const put = (x: number, y: number, c: number) => px.push([x, y, c]);
  for (let y = -2; y <= 2; y++) put(0, y, y === 0 ? STEEL_L : INK);
  put(-1, -1, INK);
  put(-1, 1, INK);
  for (let x = 1; x <= 10; x++) {
    for (let y = -3; y <= 3; y++) {
      const edge = y === -3 || y === 3 || x === 10;
      const band = x % 3 === 1;
      let color = STEEL;
      if (edge) color = INK;
      else if (band) color = y < 0 ? COPPER_L : y === 2 ? COPPER_D : COPPER;
      else if (y < 0) color = STEEL_L;
      else if (y > 1) color = STEEL_D;
      put(x, y, color);
    }
  }
  for (let x = 11; x <= 12; x++) {
    put(x, -1, INK);
    put(x, 0, x === 12 ? CORE : STEEL_L);
    put(x, 1, INK);
  }
  return px;
}

const COIL_PX = buildCoil();


function drawCanister(buf: PixBuf, light: PixBuf, coil: Coil, time: number) {
  const ang = coil.phase === "fly" ? Math.atan2(coil.vy, coil.vx || 0.001) : -Math.PI / 2;
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  const live = coil.phase === "arc";
  const frame = (time * 60) | 0;
  // Live canister hums: a one-pixel shake each frame, harder in the last half second.
  const shake = live ? (coil.left < 0.5 ? 1.4 : 0.8) : 0;
  const ox = coil.x / CELL + (hash(coil.salt, frame, 5) - 0.5) * 2 * shake;
  const oy = coil.y / CELL + (hash(coil.salt, frame, 6) - 0.5) * 2 * shake;
  for (const [lx, ly, color] of COIL_PX) {
    buf.set(Math.round(ox + lx * c - ly * s), Math.round(oy + lx * s + ly * c), color);
  }
  if (live) {
    // Crackle crawling over the copper bands.
    for (let n = 0; n < 2; n++) {
      let px = 1 + ((hash(coil.salt, frame, 10 + n) * 9) | 0);
      let py = -3 + ((hash(coil.salt, frame, 20 + n) * 6) | 0);
      for (let k = 0; k < 4; k++) {
        buf.set(Math.round(ox + px * c - py * s), Math.round(oy + px * s + py * c), k % 2 ? YELL : CORE);
        px += 1;
        py += hash(coil.salt, frame, 30 + n * 4 + k) < 0.5 ? -1 : 1;
      }
    }
    const heat = 0.35 + 0.25 * hash(coil.salt, frame, 7) + (coil.left < 0.6 ? 0.3 : 0);
    light.glow(ox + 6 * c, oy + 6 * s, 9, YELL, heat * 0.6);
  }
  const flick = hash(coil.salt, (time * 32) | 0, 3);
  const tip = 14;
  const sx = ox + tip * c;
  const sy = oy + tip * s;
  buf.set(Math.round(sx), Math.round(sy), flick > 0.45 ? CORE : YELL);
  buf.set(Math.round(sx - s), Math.round(sy + c), flick > 0.7 ? PALE : YELL);
  if (flick > 0.62) buf.set(Math.round(sx + c), Math.round(sy + s), CORE);
  light.glow(sx, sy, coil.phase === "arc" ? 9 : 6, YELL, coil.phase === "arc" ? 0.5 : 0.28);
  if (coil.phase === "arc") light.glow(ox + 6 * c, oy + 6 * s, 11, PALE, 0.2);
}

function drawBolt(buf: PixBuf, light: PixBuf, bolt: Bolt, time: number) {
  const frame = (time * 60) | 0;
  const fade = Math.max(0, bolt.life / bolt.max);
  const forks = bolt.forks ?? 1;
  // Per-frame flicker: some frames the core runs pale instead of white.
  const dim = hash(bolt.salt, frame, 1) < 0.18;
  for (let s = 1; s < bolt.pts.length; s++) {
    const seg = jag(bolt.pts[s - 1], bolt.pts[s], bolt.salt + s * 19, frame);
    if (bolt.heavy) {
      stroke(buf, seg, AMBER, 2.2);
      stroke(buf, seg, YELL, 1.4);
      stroke(buf, seg, PALE, 0.7);
    } else {
      stroke(buf, seg, AMBER, 1.5);
      stroke(buf, seg, YELL, 0.75);
    }
    stroke(buf, seg, dim ? PALE : CORE, 0);
    // Pale-yellow halo along the whole path, not just at the ends.
    for (let k = 1; k < seg.length; k += 2) light.glow(seg[k][0] / CELL, seg[k][1] / CELL, bolt.heavy ? 6 : 3.5, YELL, (bolt.heavy ? 0.34 : 0.2) * fade);
    // Forks: short jagged side branches off inner kinks, re-rolled every frame.
    let made = 0;
    for (let k = 1; k < seg.length - 1 && made < forks; k++) {
      if (hash(bolt.salt + s, frame, k * 7) > 0.6) continue;
      const [px, py] = seg[k];
      const dir = Math.atan2(seg[k + 1][1] - seg[k - 1][1], seg[k + 1][0] - seg[k - 1][0]);
      const side = hash(bolt.salt, frame, k * 13) < 0.5 ? -1 : 1;
      const a = dir + side * (0.5 + hash(bolt.salt, frame, k * 17) * 0.6);
      const l = 10 + hash(bolt.salt, frame, k * 23) * 18;
      const branch = jag([px, py], [px + Math.cos(a) * l, py + Math.sin(a) * l], bolt.salt + k * 31, frame);
      stroke(buf, branch, AMBER, bolt.heavy ? 1.2 : 0.7);
      stroke(buf, branch, YELL, 0);
      stroke(buf, branch.slice(0, 2), CORE, 0);
      // Secondary fork off the fork.
      if (bolt.heavy && branch.length > 2 && hash(bolt.salt, frame, k * 41) < 0.5) {
        const [qx, qy] = branch[1];
        const b = a + (hash(bolt.salt, frame, k * 43) < 0.5 ? -0.7 : 0.7);
        stroke(buf, jag([qx, qy], [qx + Math.cos(b) * l * 0.5, qy + Math.sin(b) * l * 0.5], bolt.salt + k * 47, frame), YELL, 0);
      }
      made++;
    }
  }
  for (let s = 1; s < bolt.pts.length; s++) {
    const x = bolt.pts[s][0] / CELL;
    const y = bolt.pts[s][1] / CELL;
    if (bolt.heavy) {
      // Impact splash: uneven rays, re-rolled every frame. Rays, not a ring.
      const rays = 7;
      for (let r = 0; r < rays; r++) {
        const a = (r / rays) * Math.PI * 2 + hash(bolt.salt, frame, 50 + r) * 0.6;
        const len = (2.5 + hash(bolt.salt, frame, 60 + r) * 4.5) * (0.6 + 0.4 * fade);
        buf.line(x, y, x + Math.cos(a) * len, y + Math.sin(a) * len, r % 2 ? PALE : YELL);
        buf.line(x, y, x + Math.cos(a) * len * 0.45, y + Math.sin(a) * len * 0.45, CORE);
      }
      light.glow(x, y, 10, PALE, 0.5 * fade);
    }
    const arm = 1.5 + hash(bolt.salt, frame, s) * 1.6;
    buf.line(x - arm, y, x + arm, y, YELL);
    buf.line(x, y - arm, x, y + arm, YELL);
    buf.line(x - arm * 0.6, y - arm * 0.6, x + arm * 0.6, y + arm * 0.6, PALE);
    buf.line(x - arm * 0.6, y + arm * 0.6, x + arm * 0.6, y - arm * 0.6, PALE);
    buf.set(Math.round(x), Math.round(y), CORE);
    light.glow(x, y, 6.5, YELL, 0.42 * fade);
  }
  const ox = bolt.pts[0][0] / CELL;
  const oy = bolt.pts[0][1] / CELL;
  light.glow(ox, oy, 5, PALE, 0.28 * fade);
}

function jag(a: [number, number], b: [number, number], salt: number, frame: number): Array<[number, number]> {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  const segs = Math.max(3, Math.min(8, Math.round(len / 28)));
  const nx = -dy / len;
  const ny = dx / len;
  const pts: Array<[number, number]> = [[a[0], a[1]]];
  for (let i = 1; i < segs; i++) {
    const u = i / segs;
    const wob = (hash(salt, frame, i) - 0.5) * Math.min(22, len * 0.18);
    pts.push([a[0] + dx * u + nx * wob, a[1] + dy * u + ny * wob]);
  }
  pts.push([b[0], b[1]]);
  return pts;
}

function stroke(buf: PixBuf, pts: Array<[number, number]>, color: number, off: number) {
  for (let i = 1; i < pts.length; i++) {
    const x0 = pts[i - 1][0] / CELL;
    const y0 = pts[i - 1][1] / CELL;
    const x1 = pts[i][0] / CELL;
    const y1 = pts[i][1] / CELL;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy) || 1;
    const ox = (-dy / len) * off;
    const oy = (dx / len) * off;
    buf.line(x0 + ox, y0 + oy, x1 + ox, y1 + oy, color);
    if (off) buf.line(x0 - ox, y0 - oy, x1 - ox, y1 - oy, color);
  }
}

// ---------------------------------------------------------------------------
// Meteor art. Everything is drawn into the fx pixel buffer (one pixel per
// CELL), re-rolled per frame where it should flicker, so every frame of the
// cast advances. No flat discs or rings: shapes are cracks, shards and dither.
// ---------------------------------------------------------------------------

const EMBER_D = hex("#b0301a");
const LAVA_D = hex("#8a2a14");
const RIFT_IN = hex("#2a040c");
const RIFT_MID = hex("#5a0a14");
const RED = hex("#d8321e");
const SMOKE_D = hex("#3a2e2c");
const SMOKE_L = hex("#5a4a44");
const CHAR_D = hex("#1e1a16");
const ROCK_HI = hex("#a8784a");

interface RockArt {
  d: number;
  c: number;
  color: Uint32Array;
  /** 0 empty, 1 rock, 2 lava crack, 3 outline. */
  kind: Uint8Array;
  /** Steps in from the outline. */
  depth: Uint8Array;
}

function rng(seed: number) {
  let n = seed | 0;
  return () => {
    n = (n + 0x6d2b79f5) | 0;
    let t = Math.imul(n ^ (n >>> 15), 1 | n);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A hand-cut angular boulder: faceted shading, chiselled facet seams, lava cracks. */
function buildRock(seed: number): RockArt {
  const rand = rng(seed);
  const R = METEOR.size;
  const c = R + 2;
  const d = c * 2 + 1;
  const verts: Array<[number, number]> = [];
  const nv = 9 + ((rand() * 3) | 0);
  for (let k = 0; k < nv; k++) {
    const a = (k / nv) * Math.PI * 2 + (rand() - 0.5) * 0.35;
    const r = R * (0.76 + rand() * 0.24);
    verts.push([Math.cos(a) * r, Math.sin(a) * r * 0.9]);
  }
  const inside = (x: number, y: number) => {
    let hit = false;
    for (let i = 0, j = nv - 1; i < nv; j = i++) {
      const [xi, yi] = verts[i];
      const [xj, yj] = verts[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
    }
    return hit;
  };
  const seeds: Array<[number, number, number]> = [];
  for (let k = 0; k < 7; k++) {
    const a = rand() * Math.PI * 2;
    const r = rand() * R * 0.8;
    seeds.push([Math.cos(a) * r, Math.sin(a) * r, rand()]);
  }
  const kind = new Uint8Array(d * d);
  const color = new Uint32Array(d * d);
  const depth = new Uint8Array(d * d);
  const tones = [CHAR, CHAR_L, UMBER, UMBER_L, ROCK_HI];
  for (let y = 0; y < d; y++) {
    for (let x = 0; x < d; x++) {
      const lx = x - c;
      const ly = y - c;
      if (!inside(lx + 0.5 - 0.5, ly)) continue;
      const i = y * d + x;
      kind[i] = 1;
      let best = 1e9;
      let next = 1e9;
      let pick = 0;
      for (let k = 0; k < seeds.length; k++) {
        const q = (lx - seeds[k][0]) ** 2 + (ly - seeds[k][1]) ** 2;
        if (q < best) {
          next = best;
          best = q;
          pick = k;
        } else if (q < next) next = q;
      }
      // Facet tone: lit from the upper left, plus each facet's own tilt.
      const [sx, sy, tilt] = seeds[pick];
      const lit = (-sx * 0.6 - sy * 0.8) / R + (tilt - 0.5) * 0.9 + (-lx * 0.6 - ly * 0.8) / (R * 2.2);
      let tone = Math.round(1.8 + lit * 2.4);
      if (Math.sqrt(next) - Math.sqrt(best) < 0.9) tone -= 1;
      if (bayer(x, y) > 0.82) tone += 1;
      color[i] = tones[Math.max(0, Math.min(tones.length - 1, tone))];
    }
  }
  // Lava cracks: random walks out from near the middle.
  for (let k = 0; k < 5; k++) {
    let x = c + (rand() - 0.5) * R * 0.5;
    let y = c + (rand() - 0.5) * R * 0.5;
    let a = rand() * Math.PI * 2;
    const len = R * (0.6 + rand() * 0.6);
    for (let s = 0; s < len; s++) {
      const i = Math.round(y) * d + Math.round(x);
      if (kind[i] !== 1) break;
      kind[i] = 2;
      if (s % 3 === 1 && rand() < 0.5) {
        const j = Math.round(y + Math.cos(a)) * d + Math.round(x - Math.sin(a));
        if (kind[j] === 1) kind[j] = 2;
      }
      a += (rand() - 0.5) * 1.1;
      x += Math.cos(a);
      y += Math.sin(a);
    }
  }
  // Outline and depth from the edge.
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < d && y < d && kind[y * d + x] !== 0;
  for (let y = 0; y < d; y++) {
    for (let x = 0; x < d; x++) {
      if (!solid(x, y)) continue;
      let dep = 9;
      for (let r = 1; r <= 4 && dep === 9; r++) {
        for (let oy = -r; oy <= r && dep === 9; oy++) {
          for (let ox = -r; ox <= r; ox++) {
            if (Math.abs(ox) !== r && Math.abs(oy) !== r) continue;
            if (!solid(x + ox, y + oy)) {
              dep = r - 1;
              break;
            }
          }
        }
      }
      depth[y * d + x] = dep;
      if (dep === 0) kind[y * d + x] = 3;
    }
  }
  return { d, c, color, kind, depth };
}

const ROCKS: RockArt[] = [buildRock(7741), buildRock(1903), buildRock(52217)];

/** Fire runes, 5x7. Invented shapes: forks, hooks and ladders, not any real script. */
const GLYPHS = [
  ["..#..", ".###.", "#.#.#", "..#..", ".#.#.", "#...#", "#...#"],
  ["#...#", ".#.#.", "..#..", "#####", "....#", "...#.", "..#.."],
  ["####.", "#...#", "####.", "#.#..", "#..#.", "#...#", "#...."],
  [".###.", "#....", ".##..", "...#.", "....#", "#..#.", ".##.."],
  ["#.#.#", "#.#.#", "#####", "..#..", ".###.", "..#..", "..#.."],
  ["#####", "....#", "..##.", ".#...", "#....", "#...#", ".###."],
  ["#....", "##...", "#.#..", "#..#.", "#.#..", "##...", "#...."],
];

function drawMeteorFx(buf: PixBuf, light: PixBuf, rock: MeteorRock, time: number) {
  const frame = (time * 60) | 0;
  if (rock.hit >= 0) drawCrater(buf, light, rock, frame);
  if (rock.phase === "fall" && !rock.cratered) drawShadow(buf, light, rock);
  drawSigil(buf, light, rock, frame);
  drawRift(buf, light, rock, frame);
  if (rock.phase === "cast") drawEmber(buf, light, rock, frame);
  if (rock.phase === "fall" || rock.phase === "roll" || rock.phase === "fade") drawRock(buf, light, rock, frame);
  if (rock.hit >= 0) drawShock(buf, light, rock, frame);
}

function drawEmber(buf: PixBuf, light: PixBuf, rock: MeteorRock, frame: number) {
  const u = rock.age / METEOR.cast;
  // Staff-tip burst for the first few frames: short uneven rays.
  if (rock.age < 0.09) {
    const ox = rock.ox / CELL;
    const oy = rock.oy / CELL;
    const k = 1 - rock.age / 0.09;
    for (let r = 0; r < 6; r++) {
      const a = (r / 6) * Math.PI * 2 + hash(rock.salt, frame, 70 + r) * 0.7;
      const len = (2 + hash(rock.salt, frame, 80 + r) * 4) * (0.5 + k);
      buf.line(ox, oy, ox + Math.cos(a) * len, oy + Math.sin(a) * len, r % 2 ? MAGMA : MAGMA_L);
    }
    buf.set(Math.round(ox), Math.round(oy), CORE);
    light.glow(ox, oy, 12, MAGMA_L, 0.7 * k);
  }
  // Trail of cooling sparks behind the seed.
  for (let k = 8; k >= 1; k--) {
    const v = u - k * 0.06;
    if (v < 0) continue;
    const [x, y] = emberAt(rock, v);
    const px = x / CELL + (hash(rock.salt, frame, k) - 0.5) * 1.6;
    const py = y / CELL + (hash(rock.salt, frame, k + 20) - 0.5) * 1.6;
    const color = k > 5 ? EMBER_D : k > 2 ? MAGMA : MAGMA_L;
    const rx = Math.round(px);
    const ry = Math.round(py);
    buf.set(rx, ry, k <= 1 ? YELL : color);
    if (k <= 5) {
      buf.set(rx + 1, ry, color);
      buf.set(rx, ry + 1, color);
    }
    if (k <= 2) {
      buf.set(rx - 1, ry, MAGMA);
      buf.set(rx, ry - 1, MAGMA);
    }
  }
  const [x, y] = emberAt(rock, u);
  const cx = Math.round(x / CELL);
  const cy = Math.round(y / CELL);
  const flick = hash(rock.salt, frame, 3);
  // Burning seed: a lumpy diamond, white core, flickering rim.
  for (let oy = -3; oy <= 3; oy++) {
    for (let ox = -3; ox <= 3; ox++) {
      const d = Math.abs(ox) + Math.abs(oy);
      if (d > 3 || (d === 3 && hash(rock.salt, frame, ox * 7 + oy) < 0.5)) continue;
      buf.set(cx + ox, cy + oy, d === 0 ? CORE : d === 1 ? (flick > 0.4 ? MAGMA_HOT : YELL) : d === 2 ? MAGMA_L : MAGMA);
    }
  }
  if (flick > 0.6) buf.set(cx + (flick > 0.8 ? 4 : -4), cy - 2, PALE);
  light.glow(cx, cy, 14, MAGMA_L, 0.6 + flick * 0.2);
}

function drawRift(buf: PixBuf, light: PixBuf, rock: MeteorRock, frame: number) {
  const open = riftOpen(rock);
  if (open <= 0.02) return;
  const cx = rock.rx / CELL;
  const cy = rock.ry / CELL;
  const half = Math.max(2, Math.round(38 * open));
  const tall = 16 * open;
  const slant = rock.fromRight ? -0.12 : 0.12;
  for (let x = -half; x <= half; x++) {
    const u = x / half;
    const jag = 0.65 + 0.7 * hash(rock.salt, x, 1);
    const h = tall * Math.pow(Math.max(0, 1 - u * u), 0.6) * jag;
    const mid = cy + x * slant + (hash(rock.salt, x >> 2, 2) - 0.5) * 2;
    const top = Math.round(mid - h * 0.45 - (hash(rock.salt, frame >> 1, x + 400) < 0.2 ? 1 : 0));
    const bot = Math.round(mid + h * 0.55 + (hash(rock.salt, frame >> 1, x + 900) < 0.25 ? 1 : 0));
    const px = Math.round(cx + x);
    if (bot - top < 1) {
      buf.set(px, Math.round(mid), Math.abs(u) > 0.85 ? RED : MAGMA_L);
      continue;
    }
    for (let y = top; y <= bot; y++) {
      const edge = Math.min(y - top, bot - y);
      let color: number;
      if (edge === 0) color = hash(rock.salt, frame, x * 7 + y) < 0.3 ? CORE : MAGMA_HOT;
      else if (edge === 1) color = YELL;
      else if (edge === 2) color = MAGMA;
      else {
        const churn = hash(rock.salt + y, frame >> 1, x);
        color = churn > 0.93 ? MAGMA_L : churn > 0.8 ? RED : edge > 3 ? RIFT_IN : RIFT_MID;
      }
      buf.set(px, y, color);
    }
    // Fire licking out of the lower lip.
    if (hash(rock.salt, x, frame >> 2) > 0.82) {
      const lick = 1 + ((hash(rock.salt, x, (frame >> 2) + 7) * 4) | 0);
      for (let k = 1; k <= lick; k++) buf.set(px, bot + k, k === 1 ? MAGMA_L : k < 3 ? MAGMA : EMBER_D);
    }
    if (hash(rock.salt, x, frame >> 1) > 0.9) buf.set(px, top - 1, MAGMA);
  }
  // Fracture lines running off both tips.
  for (const side of [-1, 1]) {
    let x = cx + side * half;
    let y = cy + side * half * slant;
    const steps = Math.round(5 + 6 * open);
    for (let k = 0; k < steps; k++) {
      x += side;
      y += (hash(rock.salt, k, side + 5) - 0.5) * 1.6;
      buf.set(Math.round(x), Math.round(y), k < steps * 0.5 ? RED : EMBER_D);
    }
  }
  light.glow(cx, cy, 34 * open, MAGMA, 0.5 * open);
  light.glow(cx, cy, 14 * open, MAGMA_L, 0.45 * open);
  for (let k = 1; k <= 3; k++) light.glow(cx + (k - 2) * half * 0.5, cy + 6 + k * 6, 10 + k * 3, MAGMA, 0.14 * open);
}

function drawSigil(buf: PixBuf, light: PixBuf, rock: MeteorRock, frame: number) {
  const a = rock.age - METEOR.cast;
  if (a < 0) return;
  const fade = rock.hit >= 0 ? 1 - rock.hit / 0.25 : 1;
  if (fade <= 0) return;
  const cx = rock.tx / CELL;
  const cy = rock.ty / CELL;
  const R = 25;
  const n = 7;
  const lit = a / 0.27;
  const fall = rock.phase === "fall" ? Math.min(1, rock.t / METEOR.fall) : 0;
  const spin0 = hash(rock.salt, 1, 1) * Math.PI * 2;
  // Charred ground under the sigil, spreading as it lights: dense in the middle, ragged at the rim.
  const spread = Math.min(1, lit * 1.4) * fade;
  const SR = R * 1.08;
  for (let y = -Math.ceil(SR); y <= Math.ceil(SR); y++) {
    for (let x = -Math.ceil(SR); x <= Math.ceil(SR); x++) {
      const th = Math.atan2(y, x);
      const edge = SR * (0.8 + 0.25 * hash(rock.salt, ((th + Math.PI) * 6) | 0, 6)) * (0.3 + 0.7 * spread);
      const d = Math.hypot(x, y) / edge;
      if (d >= 1) continue;
      const px = Math.round(cx + x);
      const py = Math.round(cy + y);
      if (bayer(px, py) >= Math.min(1, (1 - d) * 2.2) * spread) continue;
      const grit = hash(rock.salt, px, py);
      buf.set(px, py, grit > 0.93 ? CHAR_L : grit > 0.6 ? CHAR : CHAR_D);
    }
  }
  /** Stamp a crack along a polyline, lit up to fraction f; `fat` widens the first part. */
  const crack = (pts: Array<[number, number]>, f: number, fat: number, key: number) => {
    let total = 0;
    for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    const reach = total * f;
    let run = 0;
    for (let i = 1; i < pts.length && run < reach; i++) {
      const [x0, y0] = pts[i - 1];
      const dx = pts[i][0] - x0;
      const dy = pts[i][1] - y0;
      const seg = Math.hypot(dx, dy) || 1;
      const nx = -dy / seg;
      const ny = dx / seg;
      for (let s = 0; s <= seg && run + s <= reach; s += 0.5) {
        const u = (run + s) / total;
        const x = x0 + (dx / seg) * s;
        const y = y0 + (dy / seg) * s;
        const w = u < fat ? 1 : 0;
        const front = reach - (run + s) < 2.5 && f < 1;
        for (let k = -w - 1; k <= w + 1; k++) {
          const edge = Math.abs(k) === w + 1;
          const px = Math.round(x + nx * k);
          const py = Math.round(y + ny * k);
          let color: number;
          if (edge) color = u < fat ? LAVA_D : MAGMA;
          else if (front) color = CORE;
          else {
            const h = hash(rock.salt + key, frame >> 1, (u * 40) | 0);
            color = h < 0.12 + fall * 0.4 ? MAGMA_HOT : h < 0.5 ? YELL : MAGMA_L;
            if (k !== 0 && h > 0.3) color = MAGMA_L;
          }
          buf.set(px, py, color);
        }
      }
      run += seg;
    }
  };
  const jagged = (x0: number, y0: number, x1: number, y1: number, key: number, parts: number): Array<[number, number]> => {
    const out: Array<[number, number]> = [[x0, y0]];
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy) || 1;
    for (let k = 1; k < parts; k++) {
      const u = k / parts;
      const w = (hash(rock.salt, key, k) - 0.5) * 4;
      out.push([x0 + dx * u + (-dy / len) * w, y0 + dy * u + (dx / len) * w]);
    }
    out.push([x1, y1]);
    return out;
  };
  for (let i = 0; i < n; i++) {
    const ang = spin0 + (i / n) * Math.PI * 2 + (hash(rock.salt, i, 2) - 0.5) * 0.5;
    const t0 = (i / n) * 0.7;
    const f = Math.max(0, Math.min(1, (lit - t0) / 0.2));
    if (f <= 0) continue;
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const r1 = R * (0.78 + hash(rock.salt, i, 3) * 0.16);
    const spoke = jagged(cx + ca * 2, cy + sa * 2, cx + ca * r1, cy + sa * r1, i + 10, 5);
    crack(spoke, f, 0.3, i);
    // Shard: an upturned rock wedge at the spoke's end, its inner face lit by the crack.
    if (f >= 1) {
      const sx = cx + ca * (r1 + 2);
      const sy = cy + sa * (r1 + 2);
      const rise = Math.min(1, (lit - t0 - 0.2) / 0.1);
      const L = 2 + Math.round(4 * Math.max(0, rise));
      const wide = 2 + (i % 2);
      for (let l = -1; l <= L; l++) {
        for (let w = -wide; w <= wide; w++) {
          if (Math.abs(w) + Math.max(0, l - 2) > wide + 1) continue;
          const px = Math.round(sx + ca * l - sa * w);
          const py = Math.round(sy + sa * l + ca * w);
          const inner = l <= 0;
          const shade = w < 0 ? CHAR_L : w > 1 ? CHAR_D : w === 0 && l === 1 ? UMBER : CHAR;
          buf.set(px, py, inner ? (hash(rock.salt, frame >> 1, i * 9 + w) < 0.4 + fall * 0.4 ? MAGMA_L : MAGMA) : Math.abs(w) === wide || l === L ? INK : shade);
        }
      }
      light.glow(sx, sy, 7, MAGMA, 0.28 * fade);
    }
    // Fire rune between this spoke and the next.
    const gt = t0 + 0.24;
    if (lit > gt) {
      const ga = ang + Math.PI / n;
      const gx = Math.round(cx + Math.cos(ga) * R * 0.66) - 2;
      const gy = Math.round(cy + Math.sin(ga) * R * 0.66) - 3;
      const glyph = GLYPHS[(i + (rock.salt & 7)) % GLYPHS.length];
      const pop = lit - gt < 0.07;
      // Stone tablet the rune is burned into: chipped corners, lit from the upper left.
      for (let y = -2; y <= 8; y++) {
        for (let x = -2; x <= 6; x++) {
          const corner = (x < 0 || x > 4) && (y < 0 || y > 6);
          if (corner && hash(rock.salt, i, x * 13 + y) > 0.25) continue;
          const rim = x === -2 || x === 6 || y === -2 || y === 8;
          buf.set(gx + x, gy + y, rim ? INK : x + y < 3 ? CHAR_L : x + y > 9 ? CHAR_D : CHAR);
        }
      }
      for (let y = 0; y < 7; y++) {
        for (let x = 0; x < 5; x++) {
          if (glyph[y][x] !== "#") continue;
          const h = hash(rock.salt, frame >> 1, i * 31 + y * 5 + x);
          buf.set(gx + x, gy + y, pop ? CORE : h < 0.2 + fall * 0.3 ? YELL : h < 0.75 ? MAGMA_L : MAGMA);
        }
      }
      light.glow(gx + 2, gy + 3, 7, MAGMA_L, 0.26 * fade);
    }
  }
  // Seed at the centre, cracked open.
  const hot = hash(rock.salt, frame, 9);
  const rx = Math.round(cx);
  const ry = Math.round(cy);
  for (let y = -2; y <= 2; y++) {
    for (let x = -2; x <= 2; x++) {
      const d = Math.abs(x) + Math.abs(y);
      if (d > 3) continue;
      buf.set(rx + x, ry + y, d === 0 ? (hot > 0.4 ? CORE : MAGMA_HOT) : d === 1 ? YELL : d === 2 ? MAGMA_L : MAGMA);
    }
  }
  light.glow(cx, cy, 18 + fall * 16, MAGMA, (0.4 + fall * 0.45) * fade * Math.min(1, lit));
}

function drawShadow(buf: PixBuf, light: PixBuf, rock: MeteorRock) {
  const u = Math.min(1, rock.t / METEOR.fall);
  const cx = rock.tx / CELL;
  const cy = rock.ty / CELL;
  const r = 4 + 14 * u;
  const ry = r * 0.6;
  for (let y = Math.floor(-ry); y <= Math.ceil(ry); y++) {
    for (let x = Math.floor(-r); x <= Math.ceil(r); x++) {
      const d = Math.hypot(x / r, y / ry);
      if (d >= 1) continue;
      const px = Math.round(cx + x);
      const py = Math.round(cy + y + 3);
      if (bayer(px, py) < (0.2 + 0.7 * (1 - d)) * (0.35 + 0.65 * u)) buf.set(px, py, INK);
    }
  }
  light.glow(cx, cy, 10 + 18 * u, MAGMA_L, 0.2 + 0.4 * u);
}

function drawRock(buf: PixBuf, light: PixBuf, rock: MeteorRock, frame: number) {
  const art = ROCKS[rock.look % ROCKS.length];
  const fade = rock.phase === "fade" ? Math.max(0, 1 - rock.t / METEOR.fade) : 1;
  const ox = rock.x / CELL;
  const oy = rock.y / CELL;
  const sp = Math.hypot(rock.vx, rock.vy);
  const vx = sp > 1 ? rock.vx / sp : 0;
  const vy = sp > 1 ? rock.vy / sp : 1;
  const flying = rock.phase === "fall" && !rock.cratered;
  const heat = flying ? 1 : rock.phase === "fade" ? 0.2 * fade : 0.45;
  if (flying) drawTrail(buf, light, rock, ox, oy, vx, vy, frame);
  const c = Math.cos(-rock.spin);
  const s = Math.sin(-rock.spin);
  const R = art.c;
  const pulse = hash(rock.salt, frame >> 1, 4);
  for (let dy = -R; dy <= R; dy++) {
    for (let dx = -R; dx <= R; dx++) {
      const sx = Math.round(dx * c - dy * s) + art.c;
      const sy = Math.round(dx * s + dy * c) + art.c;
      if (sx < 0 || sy < 0 || sx >= art.d || sy >= art.d) continue;
      const i = sy * art.d + sx;
      const kind = art.kind[i];
      if (!kind) continue;
      const px = Math.round(ox + dx);
      const py = Math.round(oy + dy);
      if (fade < 1 && bayer(px, py) > fade) continue;
      const r = Math.hypot(dx, dy) || 1;
      const front = (dx * vx + dy * vy) / r;
      const dep = art.depth[i];
      let color = kind === 3 ? INK : kind === 2 ? (heat < 0.5 ? MAGMA : hash(rock.salt, frame >> 1, i) < 0.25 ? YELL : pulse > 0.5 ? MAGMA_L : MAGMA) : art.color[i];
      if (heat > 0.6 && front > 0.05) {
        // White-hot leading face.
        if (dep === 0) color = front > 0.4 ? CORE : MAGMA_L;
        else if (dep === 1) color = front > 0.35 ? MAGMA_HOT : MAGMA_L;
        else if (dep === 2) color = front > 0.45 ? MAGMA_HOT : front > 0.25 ? MAGMA_L : color;
        else if (dep === 3 && front > 0.55) color = MAGMA_L;
        else if (dep === 4 && front > 0.7 && bayer(px, py) < 0.5) color = MAGMA;
      } else if (heat > 0.3 && kind === 3 && front > 0.5) color = LAVA_D;
      buf.set(px, py, color);
    }
  }
  if (flying) {
    // Plasma bow just ahead of the rock.
    const ang = Math.atan2(vy, vx);
    for (let k = -9; k <= 9; k++) {
      const a = ang + (k / 9) * 1.2;
      const edge = METEOR.size * 0.92;
      const w = 1 - Math.abs(k) / 10;
      for (let l = 1; l <= 2; l++) {
        if (l === 2 && hash(rock.salt, frame, k + 40) > w * 0.8) continue;
        const px = Math.round(ox + Math.cos(a) * (edge + l));
        const py = Math.round(oy + Math.sin(a) * (edge + l));
        buf.set(px, py, l === 1 ? (hash(rock.salt, frame, k) < 0.4 ? CORE : PALE) : YELL);
      }
    }
    light.glow(ox + vx * 8, oy + vy * 8, 20, PALE, 0.5);
    light.glow(ox, oy, 26, MAGMA, 0.45);
  } else if (fade > 0.3) {
    light.glow(ox, oy, 12, MAGMA, 0.25 * heat);
  }
}

function drawTrail(buf: PixBuf, light: PixBuf, rock: MeteorRock, ox: number, oy: number, vx: number, vy: number, frame: number) {
  const L = 100;
  const R = METEOR.size;
  const nx = -vy;
  const ny = vx;
  // Trail grows out of the rift over the first frames of the fall.
  const len = Math.min(L, 16 + (rock.t / METEOR.fall) * L * 1.6);
  for (let s = 0; s < len; s++) {
    const k = s / L;
    const w = R * 0.95 * Math.pow(1 - k, 0.55) + 1;
    const wob = (hash(rock.salt, frame, s >> 2) - 0.5) * 2 * k * 3;
    const bx = ox - vx * (s + R * 0.4) + nx * wob;
    const by = oy - vy * (s + R * 0.4) + ny * wob;
    for (let j = -Math.ceil(w); j <= Math.ceil(w); j++) {
      const across = Math.abs(j) / w;
      if (across > 1) continue;
      const n = hash(rock.salt + s, frame, j + 50);
      const T = (1 - k) * Math.pow(1 - across, 0.6) + (n - 0.5) * 0.3;
      const px = Math.round(bx + nx * j);
      const py = Math.round(by + ny * j);
      let color: number;
      if (T > 0.86) color = CORE;
      else if (T > 0.72) color = PALE;
      else if (T > 0.6) color = YELL;
      else if (T > 0.47) color = MAGMA_L;
      else if (T > 0.34) color = MAGMA;
      else if (T > 0.24) color = RED;
      else if (T > 0.16) color = EMBER_D;
      else {
        if (bayer(px, py) > 0.55 - k * 0.3) continue;
        color = n > 0.5 ? SMOKE_L : SMOKE_D;
      }
      buf.set(px, py, color);
    }
    if (s % 14 === 0) light.glow(bx, by, 12 * (1 - k) + 4, MAGMA, 0.32 * (1 - k));
  }
}

function drawShock(buf: PixBuf, light: PixBuf, rock: MeteorRock, frame: number) {
  const cx = rock.hx / CELL;
  const cy = rock.hy / CELL;
  // Starburst for the first frames.
  if (rock.hit < 0.1) {
    const k = 1 - rock.hit / 0.1;
    for (let r = 0; r < 11; r++) {
      const a = (r / 11) * Math.PI * 2 + hash(rock.salt, frame, 90 + r) * 0.5;
      const len = (10 + hash(rock.salt, frame, 100 + r) * 22) * (0.5 + k * 0.7);
      buf.line(cx, cy, cx + Math.cos(a) * len, cy + Math.sin(a) * len, r % 3 ? YELL : PALE);
      buf.line(cx, cy, cx + Math.cos(a) * len * 0.5, cy + Math.sin(a) * len * 0.5, CORE);
    }
    light.glow(cx, cy, 40, PALE, 0.8 * k);
  }
  const life = 0.42;
  if (rock.hit >= life) return;
  const k = rock.hit / life;
  const ease = 1 - (1 - k) * (1 - k);
  const R = (METEOR.pit * 0.45 + METEOR.pit * 2.1 * ease) / CELL;
  const steps = Math.ceil(R * Math.PI * 2);
  for (let i = 0; i < steps; i++) {
    const n = hash(rock.salt, i >> 2, 9);
    if (n < 0.22) continue;
    const a = (i / steps) * Math.PI * 2;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    const thick = 1 + Math.round(n * 3 * (1 - k));
    for (let t = 0; t < thick; t++) {
      const rr = R - t + (hash(rock.salt, i, frame) - 0.5) * 1.2;
      const px = Math.round(cx + ca * rr);
      const py = Math.round(cy + sa * rr * 0.9);
      if (bayer(px, py) < k * 1.05) continue;
      buf.set(px, py, t === 0 ? (k < 0.35 ? PALE : UMBER_L) : t === 1 ? (k < 0.3 ? MAGMA_L : UMBER) : CHAR_L);
    }
  }
}

function drawCrater(buf: PixBuf, light: PixBuf, rock: MeteorRock, frame: number) {
  const heat = 1 - rock.hit / METEOR.smolder;
  if (heat <= 0) return;
  const cx = rock.hx / CELL;
  const cy = rock.hy / CELL;
  const R = (METEOR.pit * 0.42) / CELL;
  // Glowing lava seams across the crater floor.
  for (let k = 0; k < 6; k++) {
    let x = cx + (hash(rock.salt, k, 11) - 0.5) * R * 0.6;
    let y = cy + (hash(rock.salt, k, 12) - 0.5) * R * 0.5;
    let a = hash(rock.salt, k, 13) * Math.PI * 2;
    const len = R * (0.4 + hash(rock.salt, k, 14) * 0.6) * Math.min(1, 0.5 + heat);
    for (let s = 0; s < len; s++) {
      const h = hash(rock.salt, frame >> 2, k * 50 + s);
      const color = heat > 0.65 ? (h < 0.3 ? MAGMA_HOT : MAGMA_L) : heat > 0.35 ? (h < 0.3 ? MAGMA_L : MAGMA) : h < 0.5 ? MAGMA : LAVA_D;
      buf.set(Math.round(x), Math.round(y), color);
      a += (hash(rock.salt, k, s + 20) - 0.5) * 1.2;
      x += Math.cos(a);
      y += Math.sin(a) * 0.8;
    }
  }
  // Scattered glowing cinders.
  for (let k = 0; k < 14; k++) {
    const a = hash(rock.salt, k, 15) * Math.PI * 2;
    const r = Math.sqrt(hash(rock.salt, k, 16)) * R;
    const px = Math.round(cx + Math.cos(a) * r);
    const py = Math.round(cy + Math.sin(a) * r * 0.8);
    if (hash(rock.salt, frame >> 2, k + 300) > 0.35 + heat * 0.6) continue;
    buf.set(px, py, heat > 0.5 ? MAGMA_L : MAGMA);
  }
  light.glow(cx, cy, R * 1.7, MAGMA, 0.45 * heat);
}

/** Impact: hit-stop, caster link, fire jets, glowing debris, sparks. */
function impactFx(rock: MeteorRock, host: OrdnanceHost, heading: number) {
  rock.hit = 0;
  rock.hx = rock.x;
  rock.hy = rock.y;
  host.freeze?.(METEOR.freeze);
  host.landed?.();
  const x = rock.x;
  const y = rock.y;
  const cells = METEOR.pit / CELL;
  const fireball = (px: number, py: number, vx: number, vy: number, life: number, size: number, puff: 0 | 1 | 2) => {
    host.parts.spawn({ x: px, y: py, vx, vy, life, max: life, size: size * CELL, color: "#ff8a2a", grav: -190, rubble: false, glow: true, kind: "fireball", puff });
  };
  // Fire jets fanned upward and away from the incoming side.
  const back = Math.cos(heading) > 0 ? 1 : -1;
  for (let j = 0; j < 6; j++) {
    const a = -Math.PI / 2 + (j - 2.5) * 0.42 + back * 0.12 + (Math.random() - 0.5) * 0.15;
    const power = 0.8 + Math.random() * 0.5;
    for (let n = 0; n < 5; n++) {
      const s = (200 + n * 130 + Math.random() * 120) * power;
      const b = a + (Math.random() - 0.5) * 0.22;
      const off = Math.random() * 14;
      fireball(x + Math.cos(b) * off + (Math.random() - 0.5) * 8, y + Math.sin(b) * off, Math.cos(b) * s, Math.sin(b) * s, 0.26 + n * 0.06 + Math.random() * 0.14, cells * (0.3 - n * 0.035) * (0.8 + Math.random() * 0.4), n > 2 ? 2 : 0);
    }
  }
  for (let n = 0; n < 6; n++) {
    fireball(x + (Math.random() - 0.5) * 16, y + (Math.random() - 0.5) * 12, (Math.random() - 0.5) * 90, -30 - Math.random() * 60, 0.22 + Math.random() * 0.12, cells * (0.4 + Math.random() * 0.15), 0);
  }
  // Rock debris: glowing chunks and dark crumbs.
  for (let n = 0; n < 18; n++) {
    const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.6;
    const s = 220 + Math.random() * 520;
    const life = 0.5 + Math.random() * 0.5;
    const glowing = n < 8;
    host.parts.spawn({
      x: x + (Math.random() - 0.5) * 20, y: y - 4, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
      life, max: life, size: glowing ? 2.2 : 1.6 + Math.random() * 1.6,
      color: glowing ? (n % 2 ? "#ffb03a" : "#ff6a1a") : n % 3 ? "#3c3228" : "#6b4a2a",
      grav: GRAVITY * 0.8, rubble: !glowing, glow: glowing, kind: glowing ? "chunk" : "crumb",
    });
  }
  for (let n = 0; n < 40; n++) {
    const a = Math.random() * Math.PI * 2;
    const s = 300 + Math.random() * 760;
    const life = 0.18 + Math.random() * 0.4;
    host.parts.spawn({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 160, life, max: life, size: 1.2, color: "#ffe26b", grav: 800, rubble: false, glow: true, kind: "spark", buf: "fire" });
  }
  for (let n = 0; n < 10; n++) {
    const a = Math.PI + (n / 9) * Math.PI;
    const s = 120 + Math.random() * 120;
    host.parts.smoke(x + Math.cos(a) * 10, y, Math.cos(a) * s, Math.sin(a) * s * 0.4 - 20, 5 + Math.random() * 5, 0.8 + Math.random() * 0.5, "#4a3c34");
  }
}

/** Smoking crater: a thinning column of smoke and a few rising cinders. */
function smolder(rock: MeteorRock, dt: number, host: OrdnanceHost) {
  const heat = 1 - rock.hit / METEOR.smolder;
  if (heat <= 0 || rock.hit < 0.12) return;
  const r = METEOR.pit * 0.38;
  if (Math.random() < dt * 26 * heat) {
    host.parts.smoke(rock.hx + (Math.random() - 0.5) * r, rock.hy - 2, (Math.random() - 0.5) * 16, -30 - Math.random() * 30, 4 + Math.random() * 4, 0.9 + Math.random() * 0.6, heat > 0.5 ? "#3a302c" : "#4a4440");
  }
  if (Math.random() < dt * 10 * heat) {
    const life = 0.5 + Math.random() * 0.4;
    host.parts.spawn({ x: rock.hx + (Math.random() - 0.5) * r, y: rock.hy - 2, vx: (Math.random() - 0.5) * 30, vy: -50 - Math.random() * 60, life, max: life, size: 1.3, color: "#ff8a2a", grav: -30, rubble: false, glow: true, kind: "ember" });
  }
}
